import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Package,
  Search,
  Filter,
  LogOut,
  History,
  Settings,
  RefreshCw,
  Download,
  Upload,
  Check,
  Circle,
  Loader2,
  AlertCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Globe,
  Building2,
  Shield,
  Monitor,
  Book,
  Rocket,
  CheckCircle2,
  Plus,
  HardDrive,
  Trash2,
  FileCode,
  AlertTriangle,
  Info,
  Terminal,
  FolderOpen,
  Key,
  ArrowLeft,
  FileText,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import ExpertModeCustomizations, { buildExpertPayload } from "@/components/ExpertModeCustomizations";
import MstAdvancedOverrides from "@/components/MstAdvancedOverrides";
import UpdateControlsPanel from "@/components/UpdateControlsPanel";

// AppIcon component with hybrid fallback chain
const AppIcon = ({ app, size = "w-12 h-12" }) => {
  const [iconSrc, setIconSrc] = useState(app.icon || app.icon_fallback);
  const [showInitial, setShowInitial] = useState(false);
  
  useEffect(() => {
    // Reset when app changes
    setIconSrc(app.icon || app.icon_fallback);
    setShowInitial(false);
  }, [app.icon, app.icon_fallback, app.id]);

  const handleError = () => {
    // Try fallback if we're on primary icon
    if (iconSrc === app.icon && app.icon_fallback) {
      setIconSrc(app.icon_fallback);
    } else {
      // All failed, show initial badge
      setShowInitial(true);
    }
  };

  if (showInitial || (!app.icon && !app.icon_fallback)) {
    return (
      <div className={`${size} rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0`}>
        <span className="text-white font-bold text-lg">
          {app.name?.charAt(0)?.toUpperCase() || '?'}
        </span>
      </div>
    );
  }

  return (
    <img 
      src={iconSrc} 
      alt={`${app.name} icon`}
      className={`${size} rounded-lg object-contain flex-shrink-0 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px]`}
      onError={handleError}
    />
  );
};

// EXE uninstall strategy. ARP on: run the uninstall command registered in
// HKLM/HKCU, optionally with extra switches appended (e.g. Dell DDPM needs
// "/X /Silent"). ARP off: run a full custom command line, or re-run the
// installer with the uninstall switches when that is left blank.
const UninstallStrategyFields = ({
  testIdPrefix = "",
  useArp,
  onUseArpChange,
  extraArgs,
  onExtraArgsChange,
  customCommand,
  onCustomCommandChange,
}) => (
  <div className="mt-3 pt-3 border-t border-slate-700/60 space-y-3">
    <div className="flex items-start gap-3" data-testid={`${testIdPrefix}use-arp-uninstall-row`}>
      <button
        type="button"
        role="switch"
        aria-checked={useArp}
        onClick={() => onUseArpChange(!useArp)}
        data-testid={`${testIdPrefix}use-arp-uninstall-toggle`}
        className={`relative inline-flex mt-0.5 h-5 w-9 shrink-0 cursor-pointer rounded-full border-transparent transition-colors ${
          useArp ? 'bg-emerald-500' : 'bg-slate-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 mt-0.5 transform rounded-full bg-white shadow ring-0 transition-transform ${
            useArp ? 'translate-x-4' : 'translate-x-1'
          }`}
        />
      </button>
      <div className="flex-1">
        <div className="text-xs font-medium text-slate-200">
          Use ARP-driven uninstall (recommended)
        </div>
        <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
          {useArp
            ? 'The uninstall step will scan HKLM/HKCU Uninstall registry for a matching DisplayName and run its QuietUninstallString. Works for Firefox, Chrome, Notepad++ and most vendor EXEs.'
            : 'The uninstall step will run the custom command line below. Leave it blank to re-run the installer with the uninstall switches above.'}
        </p>
      </div>
    </div>
    {useArp ? (
      <div>
        <Label htmlFor={`${testIdPrefix}arp-extra-args`} className="text-xs text-slate-300">
          Additional uninstall switches (optional)
        </Label>
        <Input
          id={`${testIdPrefix}arp-extra-args`}
          data-testid={`${testIdPrefix}arp-extra-args-input`}
          value={extraArgs}
          onChange={(e) => onExtraArgsChange(e.target.value)}
          placeholder="/X /Silent"
          className="h-8 mt-1 text-xs font-mono bg-slate-800 border-slate-600 text-slate-100"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Appended to the uninstall command found in the registry. Switches it already has are not repeated.
        </p>
      </div>
    ) : (
      <div>
        <Label htmlFor={`${testIdPrefix}custom-uninstall-command`} className="text-xs text-slate-300">
          Custom uninstall command line (optional)
        </Label>
        <Input
          id={`${testIdPrefix}custom-uninstall-command`}
          data-testid={`${testIdPrefix}custom-uninstall-command-input`}
          value={customCommand}
          onChange={(e) => onCustomCommandChange(e.target.value)}
          placeholder='"%ProgramFiles%\Vendor\App\uninstall.exe" /S'
          className="h-8 mt-1 text-xs font-mono bg-slate-800 border-slate-600 text-slate-100"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Quote the executable path if it contains spaces. Environment variables such as %ProgramFiles% are expanded on the device.
        </p>
      </div>
    )}
  </div>
);

// Mirrors DEFAULT_MSI_PROPERTIES in backend/server.py. An override set in the
// MSI modal replaces these defaults rather than adding to them, so a
// pre-selection has to carry them too or the build would lose them.
const DEFAULT_MSI_PROPERTIES = {
  ALLUSERS: '1',
  REBOOT: 'ReallySuppress',
  ARPNOMODIFY: '1',
  ARPNOREMOVE: '1',
  ARPNOREPAIR: '1',
};

// Starting overrides for the MSI modal: nothing when there are no
// pre-selected properties (the backend defaults apply), otherwise the
// defaults plus the pre-selected ones, e.g. Splunk's AGREETOLICENSE=YES.
const initialMsiOverrides = (suggested) => {
  const entries = Object.entries(suggested || {});
  if (entries.length === 0) return {};
  const out = { ...DEFAULT_MSI_PROPERTIES };
  entries.forEach(([name, s]) => { out[name] = String(s?.value ?? ''); });
  return out;
};

// Says which MSI properties were pre-selected and why, so the change is
// visible and can be unticked before packaging.
// Problems the backend found in the MSI before packaging, e.g. a launch
// condition that refuses REBOOT=ReallySuppress (Egnyte Desktop App).
const MsiWarningsNotice = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="p-2 bg-amber-950/30 border border-amber-700 rounded text-xs" data-testid="msi-warnings-notice">
      <span className="font-medium text-amber-300">Check before packaging:</span>
      <ul className="mt-1 space-y-0.5 list-disc pl-4">
        {warnings.map((w, i) => (
          <li key={i} className="text-amber-200">{w}</li>
        ))}
      </ul>
    </div>
  );
};

const MsiSuggestedNotice = ({ suggested }) => {
  const entries = Object.entries(suggested || {});
  if (entries.length === 0) return null;
  return (
    <div className="p-2 bg-sky-950/30 border border-sky-800 rounded text-xs" data-testid="msi-suggested-notice">
      <span className="font-medium text-sky-300">Pre-selected for this MSI:</span>
      <ul className="mt-1 space-y-0.5">
        {entries.map(([name, s]) => (
          <li key={name} className="text-sky-200">
            <span className="font-mono">{name}={s?.value}</span>
            <span className="text-sky-400/80"> ({s?.reason})</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-sky-400/80">Untick or edit any of these below before packaging.</p>
    </div>
  );
};

export default function DashboardPage() {
  const { user, logout, getAccessToken, demoMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get auth headers - memoized to prevent infinite re-renders
  const getAuthHeaders = useCallback(async () => {
    try {
      const token = await getAccessToken();
      console.log("[getAuthHeaders] Token acquired:", token ? "yes" : "no (demo mode or error)");
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error("[getAuthHeaders] Failed to get access token:", error);
      return {};
    }
  }, [getAccessToken]);

  // State
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPublisher, setSelectedPublisher] = useState("");
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 12, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({ total_packages: 0, completed_packages: 0, failed_packages: 0, pending_packages: 0, available_apps: 0, updates_available: 0 });
  
  // Storage configuration status
  const [storageConfigured, setStorageConfigured] = useState(true); // Assume true until checked
  
  // Disk space state. Super admins only: the disk is the shared VM's, which
  // a customer can't act on, and /disk-usage and its Clean Up action are
  // restricted to super admins on the server (Oct 2026).
  const { isSuperAdmin } = usePermissions();
  const [diskUsage, setDiskUsage] = useState(null);
  
  // Package modal state
  const [selectedApp, setSelectedApp] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packaging, setPackaging] = useState(false);
  const [discoveringVersion, setDiscoveringVersion] = useState(false);
  
  // MSI Properties state (for pre-packaging configuration)
  const [showMsiConfig, setShowMsiConfig] = useState(false);
  const [msiPropsEnabled, setMsiPropsEnabled] = useState({}); // Properties to override (empty by default)
  const [msiSuggested, setMsiSuggested] = useState({}); // Pre-selected by the backend: { NAME: { value, reason } }
  const [msiWarnings, setMsiWarnings] = useState([]); // Problems found in the MSI before packaging
  const [downloadedMsiProps, setDownloadedMsiProps] = useState(null);
  // Phase 1 — Option E. Shortcut rows discovered in the just-downloaded
  // MSI's Shortcut table (returned by /packages/prepare). Admin ticks
  // which ones to strip; the selection flows to /packages/{id}/continue
  // as ``msi_shortcut_removals`` and the PSADT generator emits
  // Remove-ADTFile blocks for each in the post-install phase.
  const [downloadedMsiShortcuts, setDownloadedMsiShortcuts] = useState([]);
  // Map of Shortcut.Name -> bool (ticked-for-removal).
  const [shortcutRemovals, setShortcutRemovals] = useState({});
  const [msiShortcutsExpanded, setMsiShortcutsExpanded] = useState(false);
  const [preparingPackage, setPreparingPackage] = useState(false);
  const [preparedPackageId, setPreparedPackageId] = useState(null);
  const [autoUninstallPrevious, setAutoUninstallPrevious] = useState(true); // Toggle for auto-uninstall
  const [msiExpertMode, setMsiExpertMode] = useState(false); // Expert mode for MSI properties
  // Phase 1 — MSI Transform (MST) mode toggle for the packaging wizard.
  // When ON, the MSI property overrides configured on this modal get
  // baked into a generated ``alice.mst`` file placed alongside the base
  // MSI, and the PSADT script uses ``-Transforms`` instead of
  // ``-AdditionalArgumentList``. Preserves the vendor MSI byte-for-byte.
  const [useMst, setUseMst] = useState(false);
  // Handle on the Advanced table overrides panel, so Auto-update
  // suggestions can queue Registry / Feature edits into it.
  const mstOverridesRef = useRef(null);

  // Expert Mode: custom file copies + registry changes (PSADT v4)
  const [expertFiles, setExpertFiles] = useState([]);
  const [expertRegistry, setExpertRegistry] = useState([]);
  const [expertCustomMode, setExpertCustomMode] = useState(false);

  // Winget-EXE silent parameter review/override (shown above Expert Mode
  // on the Download & Configure → Configure screen). Defaults are seeded
  // from the manifest-derived values returned by /packages/prepare.
  const [wingetInstallSwitchesDefault, setWingetInstallSwitchesDefault] = useState("");
  const [wingetUninstallSwitchesDefault, setWingetUninstallSwitchesDefault] = useState("");
  const [wingetInstallSwitches, setWingetInstallSwitches] = useState("");
  const [wingetUninstallSwitches, setWingetUninstallSwitches] = useState("");
  const [wingetSwitchOverride, setWingetSwitchOverride] = useState(false);
  const [wingetIsExe, setWingetIsExe] = useState(false);
  // MSIX-only metadata surfaced on the Package Application modal so
  // the UI can (a) render an "MSIX" badge next to the version pill and
  // (b) hide the Silent Parameters card (Add-AppxProvisionedPackage
  // takes no /S switches). Populated by handlePreparePackage from the
  // /api/packages/prepare response.
  const [wingetIsMsix, setWingetIsMsix] = useState(false);
  const [wingetInstallerType, setWingetInstallerType] = useState("");
  const [wingetInstallerFilename, setWingetInstallerFilename] = useState("");
  const [wingetMsixPackageFamilyName, setWingetMsixPackageFamilyName] = useState("");
  // EXE uninstall strategy — default = ARP-driven (scan registry, run
  // QuietUninstallString). Toggle OFF to fall back to the classic
  // "setup.exe <uninstall_switches>" behaviour for the rare installer
  // that doesn't register itself in ARP.
  const [wingetUseArpUninstall, setWingetUseArpUninstall] = useState(true);
  const [wingetArpExtraArgs, setWingetArpExtraArgs] = useState("");
  const [wingetCustomUninstallCommand, setWingetCustomUninstallCommand] = useState("");
  
  // Dependencies state
  const [detectedDependencies, setDetectedDependencies] = useState([]);
  // Apps the package being prepared is a dependency of (from app relationships)
  const [dependencyOf, setDependencyOf] = useState([]);
  const [dependencyConfigs, setDependencyConfigs] = useState({}); // {package_id: {autoUninstall: false, msiProps: {}}}
  
  // Progress modal state
  const [activePackage, setActivePackage] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  
  // Custom upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileData, setUploadedFileData] = useState(null);
  const [uploadAppName, setUploadAppName] = useState("");
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadPublisher, setUploadPublisher] = useState("");
  const [uploadMsiProps, setUploadMsiProps] = useState({});
  const [uploadMsiSuggested, setUploadMsiSuggested] = useState({});
  
  // Upload type selection: 'select' | 'completed' | 'installer'
  const [uploadType, setUploadType] = useState("select");
  // When set, the upload modal is sourcing from the customer's Private
  // Repository — skip the picker and the file-upload step, show a
  // dedicated loading panel until the analyser response arrives.
  const [privateRepoMode, setPrivateRepoMode] = useState(false);
  // ---- Private Repo picker (Add new → from Private Repo) ----
  const [privateRepoCatalog, setPrivateRepoCatalog] = useState(null); // null = not loaded; [] = loaded but empty
  const [privateRepoCatalogLoading, setPrivateRepoCatalogLoading] = useState(false);
  const [privateRepoCatalogError, setPrivateRepoCatalogError] = useState(null);
  const [privateRepoRescanning, setPrivateRepoRescanning] = useState(false);
  const [privateRepoSelectedFamily, setPrivateRepoSelectedFamily] = useState(null);
  const [privateRepoSelectedVersion, setPrivateRepoSelectedVersion] = useState("");
  const [privateRepoSearch, setPrivateRepoSearch] = useState("");
  const [privateRepoSettingsEnabled, setPrivateRepoSettingsEnabled] = useState(false);
  // For completed package uploads
  const [completedPackageData, setCompletedPackageData] = useState(null);
  // For folder/zip uploads - selected main installer
  const [selectedMainInstaller, setSelectedMainInstaller] = useState("");
  const [extractedFiles, setExtractedFiles] = useState([]);
  
  // EXE-specific upload state
  const [exeInstallSwitches, setExeInstallSwitches] = useState("");
  const [exeUninstallSwitches, setExeUninstallSwitches] = useState("");
  const [exeDetectionType, setExeDetectionType] = useState("file");
  const [exeDetectionPath, setExeDetectionPath] = useState("");
  const [exeDetectionValue, setExeDetectionValue] = useState("");
  const [exeDetectionScript, setExeDetectionScript] = useState("");
  const [exeInstallContext, setExeInstallContext] = useState("system");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [exeAutoUninstallPrevious, setExeAutoUninstallPrevious] = useState(true);
  const [exeUseArpUninstall, setExeUseArpUninstall] = useState(true);
  const [exeArpExtraArgs, setExeArpExtraArgs] = useState("");
  const [exeCustomUninstallCommand, setExeCustomUninstallCommand] = useState("");

  // Track discover family name for linking uploaded packages to discovered apps
  const [discoverFamilyName, setDiscoverFamilyName] = useState("");

  // Check if Azure Storage is configured
  const checkStorageStatus = useCallback(async () => {
    if (demoMode) return;
    
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) return;
      const response = await axios.get(`${API}/packages/storage-status`, { headers });
      setStorageConfigured(response.data.configured);
    } catch (error) {
      console.error("Failed to check storage status:", error);
      // Don't block on error, assume configured
    }
  }, [getAuthHeaders, demoMode]);

  // Fetch disk usage - requires auth (skip in demo mode)
  const fetchDiskUsage = useCallback(async () => {
    // Skip in demo mode - this endpoint requires authentication. Skip for
    // anyone but a super admin: the server refuses them (403).
    if (demoMode || !isSuperAdmin) {
      setDiskUsage(null);
      return;
    }
    
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) return; // Skip if no auth token
      const response = await axios.get(`${API}/disk-usage`, { headers });
      setDiskUsage(response.data);
    } catch (error) {
      // Silently ignore auth errors in demo mode context
      if (error.response?.status !== 401) {
        console.error("Failed to fetch disk usage:", error);
      }
    }
  }, [getAuthHeaders, demoMode, isSuperAdmin]);

  // Lazy-load Private Repo catalog when the upload modal opens. Cheap
  // (single GET), and tells us whether to render the third picker card.
  useEffect(() => {
    if (showUploadModal && privateRepoCatalog === null && !privateRepoCatalogLoading) {
      fetchPrivateRepoCatalog();
    }
  }, [showUploadModal]);  // eslint-disable-line

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchQuery) params.append("search", debouncedSearchQuery);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedPublisher) params.append("publisher", selectedPublisher);
      params.append("page", pagination.page.toString());
      params.append("page_size", pagination.pageSize.toString());

      const response = await axios.get(`${API}/winget/applications?${params}`);
      setApplications(response.data.applications || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages,
      }));
      setCategories(response.data.categories || []);
      setPublishers(response.data.publishers || []);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, selectedCategory, selectedPublisher, pagination.page, pagination.pageSize]);

  // Fetch stats - requires auth (skip in demo mode)
  const fetchStats = useCallback(async () => {
    // Skip in demo mode - this endpoint requires authentication
    if (demoMode) return;
    
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) return; // Skip if no auth token
      const response = await axios.get(`${API}/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      // Silently ignore auth errors
      if (error.response?.status !== 401) {
        console.error("Failed to fetch stats:", error);
      }
    }
  }, [getAuthHeaders, demoMode]);

  useEffect(() => {
    fetchApplications();
    fetchStats();
    fetchDiskUsage();
    checkStorageStatus();
  }, [fetchApplications, fetchStats, fetchDiskUsage, checkStorageStatus]);
  
  // Handle navigation from Discover page
  useEffect(() => {
    const handleAutoPackage = async () => {
      if (location.state?.fromDiscover && location.state.autoPackage && location.state.wingetId) {
        const wingetId = location.state.wingetId;
        const discoverFamily = location.state.discoverFamilyName || '';
        toast.info(`Loading ${wingetId}...`);
        
        // Store the discover family name for linking after packaging
        if (discoverFamily) {
          setDiscoverFamilyName(discoverFamily);
        }
        
        try {
          // Fetch the app details directly by ID
          const response = await axios.get(`${API}/winget/applications/${encodeURIComponent(wingetId)}`);
          console.log("Auto-package: API response", response.data);
          
          if (response.data) {
            console.log("Auto-package: Opening modal for", response.data.name);
            // Auto-open the package modal with this app
            setSelectedApp(response.data);
            setShowPackageModal(true);
            setDiscoveringVersion(true);
            // Reset states
            setShowMsiConfig(false);
            setDownloadedMsiProps(null);
            setDownloadedMsiShortcuts([]);
            setShortcutRemovals({});
            setPreparedPackageId(null);
            setMsiPropsEnabled({});
            setMsiSuggested({});
            setMsiWarnings([]);
            setDetectedDependencies([]);
            setDependencyConfigs({});
            
            // Discover latest version
            try {
              const versionResponse = await axios.get(`${API}/winget/applications/${response.data.id}/latest-version`);
              const { latest_version, catalog_version, all_versions } = versionResponse.data;
              
              if (latest_version && latest_version !== response.data.version) {
                setSelectedApp(prev => ({
                  ...prev,
                  version: latest_version,
                  catalog_version: catalog_version,
                  all_versions: all_versions,
                  version_source: 'github'
                }));
                toast.success(`Latest version discovered: ${latest_version}`);
              }
            } catch (versionError) {
              console.warn("Could not discover latest version:", versionError);
            } finally {
              setDiscoveringVersion(false);
            }
          } else {
            toast.error(`App "${wingetId}" not found in catalog`);
            setSearchQuery(wingetId);
          }
        } catch (error) {
          console.error("Failed to fetch app for auto-package:", error);
          toast.error(`Failed to load "${wingetId}". Try searching manually.`);
          setSearchQuery(wingetId);
        }
        
        // Clear the navigation state after handling
        navigate(location.pathname, { replace: true, state: {} });
      } else if (location.state?.fromDiscover) {
        if (location.state.searchQuery) {
          setSearchQuery(location.state.searchQuery);
          toast.info(`Searching for "${location.state.searchQuery}"...`);
        } else if (location.state.customUpload && location.state.privateRepo) {
          // Private Repository: download latest blob server-side, run it
          // through the same analyser as a manual upload, then open the
          // standard custom-upload modal in "configure" mode.
          const appName = location.state.appName || '';
          const familyName = location.state.familyName || appName;
          const publisher = location.state.publisher || '';
          const targetVersion = location.state.privateRepoLatestVersion;
          setDiscoverFamilyName(familyName);
          await triggerPrivateRepoPackage(familyName, {
            app_name: appName,
            publisher,
            version: targetVersion,
          });
        } else if (location.state.customUpload) {
          const appName = location.state.appName || '';
          const familyName = location.state.familyName || appName;
          const publisher = location.state.publisher || '';
          // Auto-open upload modal with pre-filled app info
          setUploadAppName(appName);
          setUploadPublisher(publisher);
          setShowUploadModal(true);
          // Store the discover family name for linking after creation
          setDiscoverFamilyName(familyName);
          toast.info(`Upload a package for "${appName}"`);
        }
        navigate(location.pathname, { replace: true, state: {} });
      }
    };
    
    handleAutoPackage();
  }, [location.state, location.pathname, navigate]);

  // Search debounce - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Reset pagination on filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [selectedCategory, selectedPublisher]);

  // Discover latest version from GitHub when selecting an app
  const handleSelectApp = async (app) => {
    setSelectedApp(app);
    setShowPackageModal(true);
    setDiscoveringVersion(true);
    // Reset MSI config state when opening modal
    setShowMsiConfig(false);
    setDownloadedMsiProps(null);
    setPreparedPackageId(null);
    setMsiPropsEnabled({});
    setMsiSuggested({});
    setMsiWarnings([]);
    // Reset dependencies state
    setDetectedDependencies([]);
    setDependencyOf([]);
    setDependencyConfigs({});
    
    try {
      const response = await axios.get(`${API}/winget/applications/${app.id}/latest-version`);
      const { latest_version, catalog_version, all_versions } = response.data;
      
      if (latest_version && latest_version !== app.version) {
        // Update selected app with discovered version
        setSelectedApp(prev => ({
          ...prev,
          version: latest_version,
          catalog_version: catalog_version,
          all_versions: all_versions,
          version_source: 'github'
        }));
        toast.success(`Latest version discovered: ${latest_version}`);
      }
    } catch (error) {
      console.warn("Could not discover latest version:", error);
      // Continue with catalog version
    } finally {
      setDiscoveringVersion(false);
    }
  };

  // Step 1: Prepare package (download installer and check if MSI)
  const handlePreparePackage = async () => {
    console.log("[handlePreparePackage] Starting...", { selectedApp, demoMode });
    if (!selectedApp) {
      console.warn("[handlePreparePackage] No app selected, aborting");
      return;
    }

    // Check if in demo mode - packaging requires real authentication
    if (demoMode) {
      toast.error("Package creation requires Microsoft authentication. Please sign in with your Microsoft account to use this feature.");
      console.warn("[handlePreparePackage] Demo mode detected - packaging not available");
      return;
    }

    setPreparingPackage(true);
    setShowMsiConfig(false);
    setDownloadedMsiProps(null);
    
    try {
      // Get auth headers for API call
      const headers = await getAuthHeaders();
      console.log("[handlePreparePackage] Got headers:", headers ? "Authorization present" : "No auth header");
      
      // Double-check we have auth headers
      if (!headers.Authorization) {
        toast.error("Authentication required. Please sign in with Microsoft to create packages.");
        setPreparingPackage(false);
        return;
      }
      
      const requestBody = {
        winget_app_id: selectedApp.id,
        app_name: selectedApp.name,
        version: selectedApp.version,
        auto_uninstall_previous: autoUninstallPrevious,
        discover_family_name: discoverFamilyName || undefined,
      };
      console.log("[handlePreparePackage] Calling POST /api/packages/prepare with:", requestBody);
      
      // Call prepare endpoint - downloads installer and returns info
      const response = await axios.post(`${API}/packages/prepare`, requestBody, { headers });
      console.log("[handlePreparePackage] Response received:", response.data);

      const prepareData = response.data;
      setPreparedPackageId(prepareData.package_id);

      // Seed silent install/uninstall switch defaults from the manifest so
      // the user can review/override them on the Configure screen. Marker
      // for the EXE-only "Silent Parameters" card.
      const installDefault = prepareData.install_switches || "";
      const uninstallDefault = prepareData.uninstall_switches || "";
      setWingetInstallSwitchesDefault(installDefault);
      setWingetUninstallSwitchesDefault(uninstallDefault);
      setWingetInstallSwitches(installDefault);
      setWingetUninstallSwitches(uninstallDefault);
      setWingetSwitchOverride(false);
      // Distinguish EXE / MSI / MSIX. Only "true EXE" (not MSI and not
      // MSIX) enables the Silent Parameters card — Add-AppxProvisionedPackage
      // ignores /S switches so displaying an EXE-shaped card for a
      // .msixbundle would be misleading (and was reported by a customer).
      const detectedMsix = Boolean(prepareData.is_msix);
      setWingetIsMsix(detectedMsix);
      setWingetIsExe(!prepareData.is_msi && !detectedMsix);
      setWingetInstallerType(prepareData.installer_type || "");
      setWingetInstallerFilename(prepareData.installer_filename || "");
      setWingetMsixPackageFamilyName(prepareData.msix_package_family_name || "");

      // This app is a dependency of other apps: shared runtimes must not remove
      // other versions by default.
      const dependentApps = prepareData.dependency_of || [];
      setDependencyOf(dependentApps);
      if (dependentApps.length > 0) {
        setAutoUninstallPrevious(false);
      }

      // Store detected dependencies (if any)
      if (prepareData.dependencies && prepareData.dependencies.length > 0) {
        setDetectedDependencies(prepareData.dependencies);
        // Initialize dependency configs with defaults (auto-uninstall OFF for dependencies)
        const defaultConfigs = {};
        prepareData.dependencies.forEach(dep => {
          defaultConfigs[dep.package_id] = {
            // OFF by default for dependencies; manual relationships carry their own setting
            autoUninstall: !!dep.auto_uninstall,
            msiProps: {}
          };
        });
        setDependencyConfigs(defaultConfigs);
        toast.info(`Detected ${prepareData.dependencies.length} dependency/dependencies`);
      }
      
      // Check if it's an MSI installer
      if (prepareData.is_msi) {
        // Show MSI configuration modal
        // Note: msi_properties may be null if msitools is not installed on the server
        // Pre-selected properties: PROPERTY=VALUE from the winget switches
        // (Splunk: AGREETOLICENSE=YES) and licence-acceptance properties the
        // MSI defines. Shown in the modal and ticked, so the user sees them.
        const suggested = prepareData.msi_suggested_properties || {};
        setMsiSuggested(suggested);
        setMsiWarnings(prepareData.msi_warnings || []);
        if (Object.keys(suggested).length > 0) {
          setMsiPropsEnabled(initialMsiOverrides(suggested));
        }
        if (prepareData.msi_properties) {
          setDownloadedMsiProps(prepareData.msi_properties);
          toast.success(`MSI detected: ${prepareData.msi_filename}. Configure properties below.`);
        } else {
          // MSI detected but properties couldn't be extracted
          setDownloadedMsiProps({ all_properties: [], product_info: {}, original_properties: {} });
          toast.success(`MSI detected: ${prepareData.msi_filename}. Default MSI properties will be used.`);
        }
        // Phase 1 — Option E: surface the Shortcut table the backend
        // pulled out during prepare. Empty array is fine — UI hides the
        // pane in that case.
        setDownloadedMsiShortcuts(Array.isArray(prepareData.msi_shortcuts) ? prepareData.msi_shortcuts : []);
        setShortcutRemovals({});
        setShowMsiConfig(true);
      } else if (detectedMsix) {
        // MSIX / APPX branch — no /S switches to configure. Explain what
        // will actually run on-device so the user isn't confused by the
        // absence of the Silent Parameters card. Expert Mode
        // customisations (registry / extra files) still apply.
        setShowMsiConfig(true);
        const badge = (prepareData.installer_type || 'MSIX').toUpperCase();
        toast.info(
          `${badge} installer detected: ${prepareData.installer_filename || ''}. ` +
          `Will install via Add-AppxProvisionedPackage for all users — no silent switches required.`
        );
      } else if (prepareData.dependencies && prepareData.dependencies.length > 0) {
        // EXE with dependencies - wait for user to review dependencies
        setShowMsiConfig(true); // Reuse the config view to show dependency options
        toast.info(`EXE installer with dependencies detected. Review configuration below.`);
      } else {
        // EXE without dependencies — still show the configure step so Expert Mode
        // customisations (custom files / registry) can be added before packaging.
        setShowMsiConfig(true);
        toast.info(`EXE installer detected. Configure Expert Mode customisations or continue with packaging.`);
      }
    } catch (error) {
      console.error("Failed to prepare package:", error);
      toast.error(error.response?.data?.detail || "Failed to prepare package");
    } finally {
      setPreparingPackage(false);
    }
  };

  // Step 2: Continue packaging after MSI config (or directly for EXE)
  const continuePackaging = async (packageId, msiProperties) => {
    console.log("[continuePackaging] Starting...", { packageId, msiProperties });
    setPackaging(true);
    try {
      // Get auth headers for API call
      const headers = await getAuthHeaders();
      console.log("[continuePackaging] Got headers:", headers ? "Authorization present" : "No auth header");
      
      // MST table edits (e.g. Shortcut deletes) save themselves a moment
      // after each change; make sure the last one has reached the server
      // before the build reads msi_table_edits, or it would be left out.
      if (useMst && mstOverridesRef.current) {
        const saved = await mstOverridesRef.current.flush();
        if (!saved) {
          toast.error("Couldn't save the MST table edits. Check Advanced table overrides and try again.");
          return;
        }
      }

      const expertPayload = buildExpertPayload(expertFiles, expertRegistry);
      // Phase 1 — Option E (refined). Collect the admin's shortcut-
      // removal selection from the MSI configuration pane. Each
      // ticked row carries the exact on-disk location info derived
      // from the MSI's Directory/Shortcut tables (is_desktop +
      // desktop_scope, or start_menu_scope + start_menu_subpath) so
      // the backend can emit ONE precise Remove-ADTFile per entry
      // instead of blasting every plausible desktop/Start Menu path.
      const tickedShortcuts = downloadedMsiShortcuts
        .filter((sc) => shortcutRemovals[sc.shortcut])
        .map((sc) => ({
          name: sc.name || sc.shortcut,
          is_desktop: !!sc.is_desktop,
          desktop_scope: sc.desktop_scope || null,
          start_menu_scope: sc.start_menu_scope || null,
          start_menu_subpath: sc.start_menu_subpath || "",
          shortcut: sc.shortcut || null,
        }))
        .filter((sc) => sc.name);
      // Send silent switch overrides only when the user has explicitly
      // turned on override mode AND the values differ from defaults. The
      // backend treats omitted fields as "keep the manifest-derived value".
      const continuePayload = {
        msi_properties: msiProperties,
        auto_uninstall_previous: autoUninstallPrevious,
        custom_files: expertPayload.custom_files,
        registry_entries: expertPayload.registry_entries,
        msi_shortcut_removals: tickedShortcuts,
        use_mst: useMst,
        // Per-dependency settings from the Dependencies section (auto-uninstall
        // defaults to OFF so shared runtimes aren't removed).
        dependency_configs: Object.fromEntries(
          detectedDependencies.map((dep) => [
            dep.package_id,
            { auto_uninstall: !!dependencyConfigs[dep.package_id]?.autoUninstall },
          ])
        ),
      };
      if (wingetIsExe && wingetSwitchOverride) {
        const trimmedInstall = (wingetInstallSwitches || "").trim();
        const trimmedUninstall = (wingetUninstallSwitches || "").trim();
        if (trimmedInstall && trimmedInstall !== wingetInstallSwitchesDefault) {
          continuePayload.install_switches = trimmedInstall;
        }
        if (trimmedUninstall && trimmedUninstall !== wingetUninstallSwitchesDefault) {
          continuePayload.uninstall_switches = trimmedUninstall;
        }
      }
      // Always send the ARP-uninstall preference for EXE packages so the
      // backend stores it on the package doc (default True). It's a
      // separate axis from the switch override — the user might want the
      // ARP block AND custom switches, or classic setup.exe AND custom
      // switches.
      if (wingetIsExe) {
        continuePayload.use_arp_uninstall = wingetUseArpUninstall;
        // Only the field for the active strategy is sent; the other is
        // blanked so a value typed and then toggled away never applies.
        continuePayload.arp_uninstall_extra_args = wingetUseArpUninstall ? wingetArpExtraArgs.trim() : "";
        continuePayload.custom_uninstall_command = wingetUseArpUninstall ? "" : wingetCustomUninstallCommand.trim();
      }
      const response = await axios.post(`${API}/packages/${packageId}/continue`, continuePayload, { headers });
      console.log("[continuePackaging] Response received:", response.data);

      toast.success(`Package creation started for ${selectedApp.name}`);
      setShowPackageModal(false);
      setShowMsiConfig(false);
      setExpertFiles([]);
      setExpertRegistry([]);
      setExpertCustomMode(false);
      setDownloadedMsiShortcuts([]);
      setShortcutRemovals({});
      setWingetSwitchOverride(false);
      setWingetInstallSwitches("");
      setWingetUninstallSwitches("");
      setWingetInstallSwitchesDefault("");
      setWingetUninstallSwitchesDefault("");
      setWingetUseArpUninstall(true);
      setWingetArpExtraArgs("");
      setWingetCustomUninstallCommand("");
      setWingetIsExe(false);
      setUseMst(false);
      setActivePackage(response.data);
      setShowProgressModal(true);
      fetchStats();

      // Start polling for status
      pollPackageStatus(response.data.id);
    } catch (error) {
      console.error("[continuePackaging] Failed:", error);
      toast.error("Failed to continue packaging");
    } finally {
      setPackaging(false);
    }
  };

  // Handle "Create Package" button - now starts prepare flow
  const handleCreatePackage = async () => {
    console.log("[handleCreatePackage] Button clicked!", { showMsiConfig, preparedPackageId, selectedApp });
    if (showMsiConfig && preparedPackageId) {
      // User has configured MSI properties, continue with packaging
      // msiPropsEnabled now contains all the properties to override (key=value pairs)
      console.log("[handleCreatePackage] Continuing with MSI properties:", msiPropsEnabled);
      await continuePackaging(preparedPackageId, Object.keys(msiPropsEnabled).length > 0 ? msiPropsEnabled : null);
    } else {
      // Start the prepare flow
      console.log("[handleCreatePackage] Starting prepare flow...");
      await handlePreparePackage();
    }
  };

  // Poll package status
  const pollPackageStatus = async (packageId) => {
    console.log("[pollPackageStatus] Starting poll for package:", packageId);
    const poll = async () => {
      try {
        // Get auth headers for API call
        const headers = await getAuthHeaders();
        
        const response = await axios.get(`${API}/packages/${packageId}/status`, { headers });
        const { status, progress, current_step, error_message } = response.data;
        console.log(`[pollPackageStatus] status="${status}" progress=${progress} step="${current_step}"`);
        
        // Update activePackage state with the full response
        setActivePackage(prev => {
          const updated = { ...prev, ...response.data };
          return updated;
        });

        // Check completion states
        if (status === "completed") {
          console.log("[pollPackageStatus] ✅ Package COMPLETED! Stopping poll.");
          toast.success("Package created successfully!");
          fetchStats();
          return; // Stop polling
        } 
        
        if (status === "failed") {
          console.log("[pollPackageStatus] ❌ Package FAILED:", error_message);
          toast.error(`Package failed: ${error_message}`);
          return; // Stop polling
        }
        
        // Still processing - continue polling
        console.log("[pollPackageStatus] ⏳ Still processing, polling again in 2s...");
        setTimeout(poll, 2000);
        
      } catch (error) {
        console.error("[pollPackageStatus] Error:", error);
        // If unauthorized, stop polling
        if (error.response?.status === 401) {
          toast.error("Session expired. Please refresh the page.");
          return;
        }
        // For other errors, continue polling
        setTimeout(poll, 2000);
      }
    };
    poll();
  };

  // Download package
  const handleDownloadPackage = async (packageId) => {
    try {
      toast.info("Starting download...");
      
      // Use hidden iframe approach - works in sandboxed environments
      const downloadUrl = `${API}/packages/${packageId}/download`;
      
      // Create hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      
      document.body.appendChild(iframe);
      
      // Remove iframe after download starts
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 5000);
      
      // Give user feedback
      toast.success("Download started - check your downloads folder");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download package");
    }
  };

  // Refresh catalog
  const handleRefreshCatalog = async () => {
    try {
      toast.info("Refreshing catalog...");
      await axios.post(`${API}/winget/refresh`);
      await fetchApplications();
      toast.success("Catalog refreshed");
    } catch (error) {
      toast.error("Failed to refresh catalog");
    }
  };

  // Fetch the cached Private Repo catalog. Lazy — called when the user
  // opens the picker card so non-PrivateRepo tenants don't pay the
  // request cost.
  const fetchPrivateRepoCatalog = async () => {
    setPrivateRepoCatalogLoading(true);
    setPrivateRepoCatalogError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/private-repo/families`, { headers });
      const families = response.data.families || [];
      setPrivateRepoSettingsEnabled(!!(response.data.settings && response.data.settings.enabled));
      setPrivateRepoCatalog(families);
      // Auto-select the first family for convenience.
      if (families.length > 0) {
        setPrivateRepoSelectedFamily(families[0].family_name);
        setPrivateRepoSelectedVersion(families[0].latest_version || "");
      } else {
        setPrivateRepoSelectedFamily(null);
        setPrivateRepoSelectedVersion("");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      setPrivateRepoCatalogError(typeof detail === "string" ? detail : "Failed to load Private Repository catalog");
      setPrivateRepoCatalog([]);
    } finally {
      setPrivateRepoCatalogLoading(false);
    }
  };

  // Trigger a server-side rescan of the customer's blob (refreshes the
  // catalog used by the family picker).
  const rescanPrivateRepoFromModal = async () => {
    setPrivateRepoRescanning(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/private-repo/refresh`, {}, { headers });
      toast.success(
        `Private Repository rescanned: ${response.data.family_count} families, ` +
        `${response.data.version_count} versions`
      );
      await fetchPrivateRepoCatalog();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Rescan failed");
    } finally {
      setPrivateRepoRescanning(false);
    }
  };

  // Trigger a Private Repository package: server downloads the latest
  // blob, runs MSI/EXE analysis, and returns the same shape as a manual
  // /packages/upload. We then open the standard custom-upload modal in
  // configure mode (no file picker — file is already on the server).
  const triggerPrivateRepoPackage = async (familyName, opts = {}) => {
    setUploadingFile(true);
    // Skip the picker AND the file-picker stage; the file lives in blob.
    setUploadType("installer");
    setPrivateRepoMode(true);
    setShowUploadModal(true);
    setUploadAppName(opts.app_name || familyName);
    setUploadPublisher(opts.publisher || "");
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API}/private-repo/families/${encodeURIComponent(familyName)}/package`,
        {
          version: opts.version || undefined,
          app_name: opts.app_name || undefined,
          publisher: opts.publisher || undefined,
        },
        { headers },
      );
      const data = response.data;
      setUploadedFileData(data);
      if (data.product_info) {
        setUploadAppName(data.product_info.app_name || opts.app_name || familyName);
        setUploadVersion(data.product_info.version || opts.version || "");
        setUploadPublisher(data.product_info.publisher || opts.publisher || "");
      } else {
        setUploadVersion(opts.version || "");
      }
      if (data.is_exe && data.exe_analysis) {
        const analysis = data.exe_analysis;
        if (analysis.candidates && analysis.candidates.length > 0) {
          const firstCandidate = analysis.candidates[0];
          setSelectedCandidate(firstCandidate);
          setExeInstallSwitches(firstCandidate.install?.switches || "");
          setExeUninstallSwitches(firstCandidate.uninstall?.switches || "");
        }
        if (analysis.packaging_hints?.recommended_install_context) {
          setExeInstallContext(
            analysis.packaging_hints.recommended_install_context === "user" ? "user" : "system",
          );
        }
        setExeDetectionType("script");
        if (data.detection_script) setExeDetectionScript(data.detection_script);
      }
      toast.success(
        `Sourced ${familyName} v${opts.version || data.product_info?.version || ""} from Private Repository`,
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to package from Private Repository");
      setShowUploadModal(false);
      setUploadedFileData(null);
    } finally {
      setUploadingFile(false);
    }
  };

  // Custom upload handlers
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if in demo mode - upload requires real authentication
    if (demoMode) {
      toast.error("File upload requires Microsoft authentication. Please sign in with your Microsoft account to use this feature.");
      event.target.value = '';
      return;
    }
    
    // Validate file extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['msi', 'exe', 'zip'].includes(ext)) {
      toast.error("Only MSI, EXE, and ZIP files are supported");
      return;
    }
    
    setUploadingFile(true);
    
    try {
      // Get auth headers for API call
      const headers = await getAuthHeaders();
      
      const formData = new FormData();
      formData.append('file', file);
      
      // For ZIP files, use a different endpoint that extracts and lists contents
      const endpoint = ext === 'zip' ? `${API}/packages/upload-folder` : `${API}/packages/upload`;
      
      const response = await axios.post(endpoint, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const data = response.data;
      
      // Handle ZIP (folder) uploads - need to select main installer
      if (ext === 'zip' && data.extracted_files) {
        setExtractedFiles(data.extracted_files);
        setUploadedFileData({
          ...data,
          is_folder: true,
          folder_upload_id: data.upload_id
        });
        // Auto-select setup.exe or first exe/msi if found
        const installers = data.extracted_files.filter(f => 
          f.toLowerCase().endsWith('.exe') || f.toLowerCase().endsWith('.msi')
        );
        const setupFile = installers.find(f => f.toLowerCase().includes('setup')) || installers[0];
        if (setupFile) {
          setSelectedMainInstaller(setupFile);
        }
        toast.success(`Folder extracted: ${data.extracted_files.length} files found`);
        return;
      }
      
      // Set uploaded data
      setUploadedFileData(data);
      setUploadAppName(data.product_info.app_name);
      setUploadVersion(data.product_info.version);
      setUploadPublisher(data.product_info.publisher || '');
      // No overrides unless the backend pre-selected properties (e.g. an
      // AGREETOLICENSE the MSI defines); then the defaults plus those.
      setUploadMsiSuggested(data.msi_suggested_properties || {});
      setUploadMsiProps(initialMsiOverrides(data.msi_suggested_properties));
      
      // Handle EXE-specific data
      if (data.is_exe && data.exe_analysis) {
        const analysis = data.exe_analysis;
        // Set default candidate switches if available
        if (analysis.candidates && analysis.candidates.length > 0) {
          const firstCandidate = analysis.candidates[0];
          setSelectedCandidate(firstCandidate);
          setExeInstallSwitches(firstCandidate.install?.switches || '');
          setExeUninstallSwitches(firstCandidate.uninstall?.switches || '');
        } else {
          setExeInstallSwitches('');
          setExeUninstallSwitches('');
        }
        // Set install context from packaging hints
        if (analysis.packaging_hints?.recommended_install_context) {
          setExeInstallContext(analysis.packaging_hints.recommended_install_context === 'user' ? 'user' : 'system');
        }
        // Set detection to script by default with pre-generated script
        setExeDetectionType('script');
        setExeDetectionPath('');
        setExeDetectionValue('');
        // Use pre-generated detection script from backend
        if (data.detection_script) {
          setExeDetectionScript(data.detection_script);
        } else {
          setExeDetectionScript('');
        }
      }
      
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      console.error("Upload failed:", error);
      
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(detail.message || "Upload failed");
      } else {
        toast.error(detail || "Failed to upload file");
      }
      
      // Reset upload modal on error
      setUploadedFileData(null);
    } finally {
      setUploadingFile(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleContinueUpload = async () => {
    if (!uploadedFileData) return;
    
    setPackaging(true);
    
    try {
      // Get auth headers for API call
      const headers = await getAuthHeaders();
      
      // Expert Mode payload — same shape the winget /continue endpoint
      // accepts (custom_files + registry_entries). Applied to BOTH MSI
      // and EXE custom uploads now that the backend understands it.
      const expertPayload = buildExpertPayload(expertFiles, expertRegistry);
      
      let response;
      
      if (uploadedFileData.is_exe) {
        // EXE packaging flow
        response = await axios.post(`${API}/packages/upload/continue-exe`, {
          package_id: uploadedFileData.package_id,
          app_name: uploadAppName,
          version: uploadVersion,
          publisher: uploadPublisher,
          install_switches: exeInstallSwitches,
          uninstall_switches: exeUninstallSwitches,
          detection_type: exeDetectionType,
          detection_path: exeDetectionPath,
          detection_value: exeDetectionValue,
          detection_script: exeDetectionScript,
          install_context: exeInstallContext,
          auto_uninstall_previous: exeAutoUninstallPrevious,
          use_arp_uninstall: exeUseArpUninstall,
          arp_uninstall_extra_args: exeUseArpUninstall ? exeArpExtraArgs.trim() : "",
          custom_uninstall_command: exeUseArpUninstall ? "" : exeCustomUninstallCommand.trim(),
          custom_files: expertPayload.custom_files,
          registry_entries: expertPayload.registry_entries,
        }, { headers });
      } else {
        // MSI packaging flow
        response = await axios.post(`${API}/packages/upload/continue`, {
          package_id: uploadedFileData.package_id,
          app_name: uploadAppName,
          version: uploadVersion,
          publisher: uploadPublisher,
          msi_properties: uploadMsiProps,
          custom_files: expertPayload.custom_files,
          registry_entries: expertPayload.registry_entries,
        }, { headers });
      }
      
      toast.success(`Package created: ${uploadAppName}`);
      
      // Link to discovered app if this upload originated from the Discover page
      if (discoverFamilyName && response.data?.id) {
        await linkPackageToDiscoverApp(response.data.id, discoverFamilyName);
      }

      // Close modal and reset state
      resetUploadModal();
      
      // Navigate to packages page
      navigate('/packages');
      
    } catch (error) {
      console.error("Packaging failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create package");
    } finally {
      setPackaging(false);
    }
  };

  // Handle completed package upload (.intunewin or .zip)
  const handleCompletedPackageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (demoMode) {
      toast.error("Package upload requires Microsoft authentication.");
      event.target.value = '';
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['intunewin', 'zip'].includes(ext)) {
      toast.error("Please upload a .intunewin or .zip file");
      event.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('package_type', ext === 'intunewin' ? 'intunewin' : 'psadt');

      const response = await axios.post(`${API}/packages/upload-completed`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      setCompletedPackageData({
        ...response.data,
        filename: file.name,
        file_size_mb: (file.size / (1024 * 1024)).toFixed(2),
        package_type: ext === 'intunewin' ? 'IntuneWin Package' : 'PSADT Package'
      });

      // Try to extract name and version from filename
      const nameMatch = file.name.match(/^([^_]+)_([^_]+)/);
      if (nameMatch) {
        setUploadAppName(nameMatch[1].replace(/-/g, ' '));
        setUploadVersion(nameMatch[2]);
      }

      toast.success("Package uploaded successfully");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload package");
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  // Link a newly created package to a discovered app family
  const linkPackageToDiscoverApp = async (packageId, familyName) => {
    if (!familyName) return;
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/discover/mark-packaged`, {
        display_name_normalized: familyName,
        package_id: packageId
      }, { headers });
      console.log(`Linked package ${packageId} to discover family: ${familyName}`);
    } catch (error) {
      console.warn("Could not link package to discovered app:", error);
    }
  };

  // Create package record for completed package
  const handleCreateCompletedPackage = async () => {
    if (!completedPackageData || !uploadAppName || !uploadVersion) {
      toast.error("Please fill in the required fields");
      return;
    }

    setUploadingFile(true);
    try {
      const headers = await getAuthHeaders();
      
      const response = await axios.post(`${API}/packages/create-from-completed`, {
        upload_id: completedPackageData.upload_id,
        app_name: uploadAppName,
        version: uploadVersion,
        publisher: uploadPublisher || "Unknown",
        package_type: completedPackageData.package_type?.includes('IntuneWin') ? 'intunewin' : 'psadt'
      }, { headers });

      // Link to discovered app if this upload originated from the Discover page
      if (discoverFamilyName && response.data?.id) {
        await linkPackageToDiscoverApp(response.data.id, discoverFamilyName);
      }

      toast.success(`Package "${uploadAppName}" added to ALICE`);
      resetUploadModal();
      navigate('/packages');
    } catch (error) {
      console.error("Create package failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create package");
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle folder (ZIP) package creation
  const handleFolderPackage = async () => {
    if (!selectedMainInstaller || !uploadAppName || !uploadVersion) {
      toast.error("Please select a main installer and fill in the required fields");
      return;
    }

    setPackaging(true);
    try {
      const headers = await getAuthHeaders();
      
      const response = await axios.post(`${API}/packages/create-from-folder`, {
        upload_id: uploadedFileData.folder_upload_id,
        main_installer: selectedMainInstaller,
        app_name: uploadAppName,
        version: uploadVersion,
        publisher: uploadPublisher || "Unknown"
      }, { headers });

      // Link to discovered app if this upload originated from the Discover page
      if (discoverFamilyName && response.data?.id) {
        await linkPackageToDiscoverApp(response.data.id, discoverFamilyName);
      }

      toast.success(`Package "${uploadAppName}" created successfully`);
      resetUploadModal();
      navigate('/packages');
    } catch (error) {
      console.error("Folder package failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create package");
    } finally {
      setPackaging(false);
    }
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setUploadedFileData(null);
    setUploadAppName("");
    setUploadVersion("");
    setUploadPublisher("");
    setUploadMsiProps({});
    setUploadMsiSuggested({});
    // Reset upload type selection
    setUploadType("select");
    setPrivateRepoMode(false);
    // Private Repo picker state
    setPrivateRepoSelectedFamily(null);
    setPrivateRepoSelectedVersion("");
    setPrivateRepoSearch("");
    // Expert Mode (custom files + registry entries) — reset so the next
    // open of the modal starts clean.
    setExpertFiles([]);
    setExpertRegistry([]);
    setExpertCustomMode(false);
    setCompletedPackageData(null);
    setSelectedMainInstaller("");
    setExtractedFiles([]);
    // Reset EXE state
    setExeInstallSwitches("");
    setExeUninstallSwitches("");
    setExeDetectionType("file");
    setExeDetectionPath("");
    setExeDetectionValue("");
    setExeDetectionScript("");
    setExeInstallContext("system");
    setSelectedCandidate(null);
    setExeAutoUninstallPrevious(true);
    setExeUseArpUninstall(true);
    setExeArpExtraArgs("");
    setExeCustomUninstallCommand("");
    setDiscoverFamilyName("");
  };

  const getStatusIcon = (step, completedSteps, currentStep) => {
    if (completedSteps?.includes(step)) {
      return <Check className="h-4 w-4 text-white" />;
    }
    if (currentStep === step || step === currentStep) {
      return <Loader2 className="h-4 w-4 text-white animate-spin" />;
    }
    return <Circle className="h-4 w-4" />;
  };

  const getStatusIconClass = (step, completedSteps, currentStep) => {
    if (completedSteps?.includes(step)) {
      return "bg-green-500";
    }
    if (currentStep === step || step === currentStep) {
      return "bg-primary";
    }
    return "bg-muted";
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Main Content */}
      <main className="p-6 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Catalog</h1>
            <p className="text-white/90 mt-1">Browse and package applications from the winget repository</p>
          </div>
        </div>

        {/* Disk Space Warning Banner */}
        {diskUsage && (diskUsage.warning || diskUsage.critical) && (
          <div 
            className={`mb-4 p-4 rounded-lg border flex items-center justify-between ${
              diskUsage.critical 
                ? 'bg-red-900/30 border-red-700 text-red-300' 
                : 'bg-yellow-900/30 border-yellow-700 text-yellow-300'
            }`}
            data-testid="disk-warning-banner"
          >
            <div className="flex items-center gap-3">
              <HardDrive className={`h-5 w-5 ${diskUsage.critical ? 'text-red-400' : 'text-yellow-400'}`} />
              <div>
                <p className="font-medium">
                  {diskUsage.critical ? 'Critical: Disk Space Very Low!' : 'Warning: Disk Space Low'}
                </p>
                <p className="text-sm opacity-80">
                  {diskUsage.free_mb < 1000 
                    ? `${Math.round(diskUsage.free_mb)} MB free` 
                    : `${diskUsage.free_gb} GB free`} 
                  {' '}({diskUsage.usage_percent}% used) — {diskUsage.message}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className={diskUsage.critical ? 'border-red-400/50 text-red-300 hover:bg-red-500/20' : 'border-yellow-400/50 text-yellow-300 hover:bg-yellow-500/20'}
              onClick={async () => {
                if (window.confirm('This will delete all failed and incomplete packages to free up space. Continue?')) {
                  try {
                    const response = await axios.post(`${API}/disk-usage/cleanup`, null, {
                      params: { delete_failed: true }
                    });
                    toast.success(`Cleanup complete! Freed ${response.data.freed_mb} MB`);
                    fetchDiskUsage();
                    fetchStats();
                  } catch (error) {
                    toast.error('Cleanup failed: ' + (error.response?.data?.detail || error.message));
                  }
                }
              }}
              data-testid="cleanup-disk-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clean Up
            </Button>
          </div>
        )}

        {/* Azure Storage Not Configured Warning */}
        {!storageConfigured && !demoMode && (
          <div 
            className="mb-4 p-4 rounded-lg border bg-orange-900/30 border-orange-700 text-orange-300 flex items-center justify-between"
            data-testid="storage-warning-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
              <div>
                <p className="font-medium">Azure Storage Not Configured</p>
                <p className="text-sm opacity-80">
                  You must configure Azure Blob Storage in Settings before you can package applications.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-orange-400/50 text-orange-300 hover:bg-orange-500/20"
              onClick={() => navigate('/settings')}
            >
              <Settings className="h-4 w-4 mr-1" />
              Configure Storage
            </Button>
          </div>
        )}

        {/* Stats - Glass Effect */}
        <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-available" title="Approximate count of distinct packages in the winget community repository">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Available</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-400/20">
                <Globe className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">9,000+</div>
            <div className="text-sm text-white/90">Apps</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Completed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-400/20">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.completed_packages}</div>
            <div className="text-sm text-white/90">Packages</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">In Progress</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-400/20">
                <Loader2 className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.pending_packages}</div>
            <div className="text-sm text-white/90">Processing</div>
          </div>
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6" data-testid="stat-failed">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Failed</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-400/20">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.failed_packages}</div>
            <div className="text-sm text-white/90">Errors</div>
          </div>
          <Link to="/packages" className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-6 hover:border-orange-400/50 transition-colors" data-testid="stat-updates">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium uppercase tracking-wider text-white/90">Updates</span>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-400/20">
                <Download className="w-5 h-5 text-orange-400" />
              </div>
            </div>
            <div className="text-4xl font-bold text-white mb-1">{stats.updates_available || 0}</div>
            <div className="text-sm text-white/90">Available</div>
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
            <Input
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white placeholder:text-white/70"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] h-10 border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedPublisher || "all"} onValueChange={(v) => setSelectedPublisher(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] h-10 border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white" data-testid="publisher-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Publisher" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Publishers</SelectItem>
                {publishers.map((pub) => (
                  <SelectItem key={pub} value={pub}>{pub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefreshCatalog}
              className="h-10 w-10 bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
              data-testid="refresh-catalog"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            
            <Button
              variant="default"
              onClick={() => setShowUploadModal(true)}
              className="h-10 bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
              data-testid="upload-custom"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Custom
            </Button>
          </div>
        </div>

        {/* Applications Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : applications.length === 0 ? (
          <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border border-[rgba(229,239,250,0.1)] rounded-xl p-12 text-center shadow-lg shadow-black/15">
            <Package className="h-12 w-12 mb-4 text-[#E5EFFA]/30 mx-auto" />
            <p className="text-white/90">No applications found</p>
            <p className="text-sm text-white/70">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {applications.map((app) => (
              <div
                key={app.id}
                className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-[rgba(229,239,250,0.1)] rounded-xl p-4 cursor-pointer hover:border-cyan-400/50 transition-colors"
                onClick={() => handleSelectApp(app)}
                data-testid={`app-card-${app.id}`}
              >
                  <div className="flex items-start gap-3 mb-3">
                    {/* App Icon with Fallback Chain */}
                    <AppIcon 
                      app={app} 
                      size="w-12 h-12"
                    />
                    
                    {/* App Info */}
                    <div className="flex-1 min-w-0 flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate" title={app.name}>
                          {app.name}
                        </h3>
                        <p className="text-sm text-white/90 truncate">{app.publisher}</p>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="ml-2 font-mono text-xs flex-shrink-0 bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] text-white"
                        title="Catalog version (may be outdated - check official page)"
                      >
                        v{app.version}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-sm text-white/80 line-clamp-2 mb-2 min-h-[40px]">
                    {app.description || "No description available"}
                  </p>
                  
                  {/* Version disclaimer & Official link */}
                  {app.homepage && (
                    <a
                      href={app.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1 mb-3"
                      title="View official website to verify latest version"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Official Page
                    </a>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {app.tags?.slice(0, 2).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs text-white/90 border-[rgba(229,239,250,0.1)]">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/apps/${encodeURIComponent(app.id)}`);
                        }}
                        data-testid={`details-btn-${app.id}`}
                        className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                      >
                        <History className="h-3 w-3 mr-1" />
                        Details
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectApp(app);
                        }}
                        data-testid={`package-btn-${app.id}`}
                      >
                        <Package className="h-3 w-3 mr-1" />
                        Package
                      </Button>
                    </div>
                  </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              data-testid="prev-page"
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-white/90 px-4">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.totalPages}
              data-testid="next-page"
              className="bg-[#E5EFFA] text-[#006AF2] border-none hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Catalog Disclaimer */}
        <div className="mt-8 mb-4 text-center">
          <p className="text-xs text-white/70">
            Version data from community catalog (winget.run) - may lag behind official releases. 
            Click &quot;Official Page&quot; to verify latest versions.
          </p>
        </div>
      </main>

      {/* Package Modal */}
      <Dialog open={showPackageModal} onOpenChange={setShowPackageModal}>
        {/* grid-cols-[minmax(0,1fr)]: DialogContent is a CSS grid, and a
            grid track never shrinks below its content's min width by
            default. A wide table (the 16-column MSI Shortcut table) then
            stretched the whole modal sideways instead of scrolling inside
            its own overflow-x-auto box. */}
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Package Application
            </DialogTitle>
            <DialogDescription>
              Create a PSADT package for {selectedApp?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6 py-4">
              {/* App Info */}
              <div className="bg-muted/50 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{selectedApp.name}</span>
                  <div className="flex items-center gap-2">
                    {/* Installer-type badge — surfaces MSI / EXE / MSIX so
                        the user isn't surprised by what runs on-device.
                        Rendered next to the version pill; only visible
                        after the prepare call has resolved. */}
                    {wingetInstallerType && (
                      <Badge
                        variant="outline"
                        className={
                          wingetIsMsix
                            ? "font-mono border-purple-500/60 text-purple-300 bg-purple-500/10"
                            : wingetInstallerType === "msi"
                            ? "font-mono border-cyan-500/60 text-cyan-300 bg-cyan-500/10"
                            : "font-mono border-amber-500/60 text-amber-300 bg-amber-500/10"
                        }
                        data-testid="installer-type-badge"
                        title={
                          wingetIsMsix
                            ? "MSIX/APPX installer — installs via Add-AppxProvisionedPackage for all users. No silent switches required."
                            : `${wingetInstallerType.toUpperCase()} installer`
                        }
                      >
                        {wingetInstallerType.toUpperCase()}
                      </Badge>
                    )}
                    {discoveringVersion ? (
                      <Badge variant="outline" className="font-mono animate-pulse">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Checking...
                      </Badge>
                    ) : (
                      <Badge 
                        variant={selectedApp.version_source === 'github' ? 'default' : 'secondary'} 
                        className="font-mono"
                        title={selectedApp.version_source === 'github' ? 'Latest version from GitHub' : 'Version from catalog'}
                      >
                        {selectedApp.version_source === 'github' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                        v{selectedApp.version}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-white/90">{selectedApp.publisher}</p>
                {selectedApp.version_source === 'github' && selectedApp.catalog_version && selectedApp.catalog_version !== selectedApp.version && (
                  <p className="text-xs text-green-400">
                    ✓ Updated from catalog version {selectedApp.catalog_version}
                  </p>
                )}
              </div>

              {/* Package Info */}
              <div className="space-y-3">
                <div className="p-4 border bg-background">
                  <div className="flex items-start gap-3">
                    <Package className="h-5 w-5 text-[#002FA7] mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">PSADT Package</p>
                      <p className="text-xs text-white/90 mt-1">
                        Creates a PowerShell App Deployment Toolkit (v4) package with Invoke-AppDeployToolkit.ps1 and all required files.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border bg-muted/30">
                  <div className="flex items-start gap-3">
                    <Upload className="h-5 w-5 text-white/90 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-white/90">Intune Deployment</p>
                      <p className="text-xs text-white/90 mt-1">
                        After the PSADT package is created, you can upload it to Intune from the Packages page. The .intunewin file will be generated during the upload process.
                      </p>
                    </div>
                  </div>
                </div>
                
                {dependencyOf.length > 0 && (
                  <div className="p-3 border border-blue-500/40 bg-blue-500/10 rounded text-xs text-white/90" data-testid="dependency-of-banner">
                    <span className="px-1.5 py-0.5 mr-2 text-[10px] font-semibold bg-blue-500/20 text-blue-300 rounded">DEPENDENCY</span>
                    This app is a dependency of <span className="font-semibold">{dependencyOf.join(", ")}</span>. It will be packaged as a dependency and
                    linked when those apps are deployed. Auto-uninstall of previous versions is off by default so other apps keep working.
                  </div>
                )}

                {/* Auto-Uninstall Previous Versions Toggle */}
                <div className="flex items-center justify-between p-4 border bg-background">
                  <div className="space-y-0.5">
                    <Label htmlFor="winget-auto-uninstall-toggle" className="text-sm font-medium cursor-pointer">
                      Auto-uninstall previous versions
                    </Label>
                    <p className="text-xs text-white/90">
                      Automatically remove older versions before installing (recommended)
                    </p>
                  </div>
                  <Switch
                    id="winget-auto-uninstall-toggle"
                    checked={autoUninstallPrevious}
                    onCheckedChange={setAutoUninstallPrevious}
                  />
                </div>
                
                {/* Dependencies Section - Show when dependencies are detected */}
                {detectedDependencies.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-blue-500/10 px-4 py-2 border-b">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-blue-400" />
                        <span className="text-sm font-medium text-blue-400">
                          Dependencies Detected ({detectedDependencies.length})
                        </span>
                      </div>
                    </div>
                    <div className="p-3 space-y-3">
                      <p className="text-xs text-white/90">
                        These dependencies will be packaged and deployed automatically. Configure settings for each dependency below.
                      </p>
                      {detectedDependencies.map((dep, index) => (
                        <div key={dep.package_id} className="border rounded p-3 bg-muted/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500/20 text-blue-400 rounded">
                                DEPENDENCY
                              </span>
                              <span className="text-sm font-medium">{dep.name || dep.package_id}</span>
                              {dep.name && <span className="text-xs text-white/70 font-mono">{dep.package_id}</span>}
                              <span
                                className="px-1.5 py-0.5 text-[10px] rounded bg-white/10 text-white/80"
                                title={dep.source === "manual" ? "Added manually on the app's details page" : "Declared in the winget manifest"}
                              >
                                {dep.source === "manual" ? "Manual" : "winget"}
                              </span>
                              {dep.min_version && (
                                <span className="text-xs text-white/90">
                                  (min: {dep.min_version})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor={`dep-auto-uninstall-${index}`} className="text-xs cursor-pointer">
                                Auto-uninstall previous versions
                              </Label>
                              <p className="text-[10px] text-white/90">
                                Disabled by default for dependencies to avoid removing shared runtimes
                              </p>
                            </div>
                            <Switch
                              id={`dep-auto-uninstall-${index}`}
                              checked={dependencyConfigs[dep.package_id]?.autoUninstall || false}
                              onCheckedChange={(checked) => {
                                setDependencyConfigs(prev => ({
                                  ...prev,
                                  [dep.package_id]: {
                                    ...prev[dep.package_id],
                                    autoUninstall: checked
                                  }
                                }));
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Configuration step — shown after Download & Configure for BOTH MSI and EXE */}
              {selectedApp && showMsiConfig && (
                <div className="space-y-3">
                  {/* MSIX / APPX info card — the /S concept doesn't exist
                      for AppX. Shown INSTEAD of the Silent Parameters
                      card so the user has an accurate mental model of
                      what will run on-device. */}
                  {wingetIsMsix && (
                    <div
                      className="border border-purple-700/70 rounded-lg overflow-hidden"
                      data-testid="msix-info-card"
                    >
                      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-900/40 to-slate-850">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-900/60">
                            <Package className="h-4 w-4 text-purple-300" />
                          </div>
                          <div>
                            <h4 className="font-medium text-sm text-slate-200">
                              MSIX / APPX Installer
                            </h4>
                            <p className="text-xs text-slate-400">
                              No silent switches — this format installs via <span className="font-mono text-purple-300">Add-AppxProvisionedPackage</span> for all users.
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className="font-mono border-purple-500/60 text-purple-300 bg-purple-500/10"
                          data-testid="msix-info-type-badge"
                        >
                          {(wingetInstallerType || "MSIX").toUpperCase()}
                        </Badge>
                      </div>
                      <div className="p-3 border-t border-purple-700/50 bg-slate-900/60 space-y-2">
                        {wingetInstallerFilename && (
                          <div className="text-xs">
                            <span className="text-slate-400">Installer file: </span>
                            <span className="font-mono text-slate-200" data-testid="msix-info-filename">
                              {wingetInstallerFilename}
                            </span>
                            <span className="text-slate-500"> (shipped inside PSADT </span>
                            <span className="font-mono text-slate-400">Files\</span>
                            <span className="text-slate-500"> — installed from the LOCAL device copy)</span>
                          </div>
                        )}
                        {wingetMsixPackageFamilyName ? (
                          <div className="text-xs">
                            <span className="text-slate-400">Package Family Name: </span>
                            <span className="font-mono text-slate-200 break-all" data-testid="msix-info-pfn">
                              {wingetMsixPackageFamilyName}
                            </span>
                          </div>
                        ) : (
                          <p className="text-xs text-amber-300/90" data-testid="msix-info-pfn-missing">
                            ⚠ PackageFamilyName not declared in this manifest — uninstall will fall back to short-name matching.
                          </p>
                        )}
                        <p className="text-[11px] text-slate-500 italic leading-relaxed">
                          On-device install: <span className="font-mono text-slate-400">Add-AppxProvisionedPackage&nbsp;-Online&nbsp;-PackagePath&nbsp;$adtSession.DirFiles\{wingetInstallerFilename || "…"}&nbsp;-SkipLicense</span>. <span className="text-slate-600">(</span><span className="font-mono text-slate-400">-Online</span><span className="text-slate-600"> targets the running OS — nothing is fetched over the network.)</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Silent Parameters (EXE only) — let the user review the
                      install/uninstall switches that will be baked into
                      PSADT before packaging starts. Defaults come from the
                      winget manifest; an explicit override toggle unlocks
                      the inputs so they can be edited. */}
                  {wingetIsExe && (
                    <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid="silent-params-card">
                      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${wingetSwitchOverride ? 'bg-amber-900/50' : 'bg-slate-700'}`}>
                            <Terminal className={`h-4 w-4 ${wingetSwitchOverride ? 'text-amber-300' : 'text-slate-300'}`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-sm text-slate-200">Silent Parameters</h4>
                            <p className="text-xs text-slate-400">
                              {wingetSwitchOverride
                                ? 'Override active — these switches will be written into PSADT.'
                                : 'Manifest-derived defaults. Toggle override to edit before packaging.'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 hidden sm:inline">Override</span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={wingetSwitchOverride}
                            data-testid="silent-params-override-toggle"
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              wingetSwitchOverride ? 'bg-amber-500' : 'bg-slate-600'
                            }`}
                            onClick={() => {
                              const next = !wingetSwitchOverride;
                              setWingetSwitchOverride(next);
                              if (!next) {
                                // Re-sync to the manifest defaults when override is turned off
                                setWingetInstallSwitches(wingetInstallSwitchesDefault);
                                setWingetUninstallSwitches(wingetUninstallSwitchesDefault);
                              }
                            }}
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                wingetSwitchOverride ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                      <div className="p-3 border-t border-slate-700 bg-slate-900/60 space-y-3">
                        <div>
                          <Label htmlFor="winget-install-switches" className="text-xs text-slate-300">
                            Install switches
                          </Label>
                          <Input
                            id="winget-install-switches"
                            data-testid="silent-params-install-input"
                            value={wingetInstallSwitches}
                            disabled={!wingetSwitchOverride}
                            onChange={(e) => setWingetInstallSwitches(e.target.value)}
                            placeholder={wingetInstallSwitchesDefault || '/S'}
                            className="h-8 mt-1 text-xs font-mono bg-slate-800 border-slate-600 text-slate-100 disabled:opacity-80 disabled:text-slate-300"
                          />
                          {wingetInstallSwitchesDefault && (
                            <p className="text-[11px] text-slate-500 mt-1 font-mono">
                              Default: <span className="text-slate-400">{wingetInstallSwitchesDefault}</span>
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="winget-uninstall-switches" className="text-xs text-slate-300">
                            Uninstall switches
                          </Label>
                          <Input
                            id="winget-uninstall-switches"
                            data-testid="silent-params-uninstall-input"
                            value={wingetUninstallSwitches}
                            disabled={!wingetSwitchOverride}
                            onChange={(e) => setWingetUninstallSwitches(e.target.value)}
                            placeholder={wingetUninstallSwitchesDefault || '/S'}
                            className="h-8 mt-1 text-xs font-mono bg-slate-800 border-slate-600 text-slate-100 disabled:opacity-80 disabled:text-slate-300"
                          />
                          {wingetUninstallSwitchesDefault && (
                            <p className="text-[11px] text-slate-500 mt-1 font-mono">
                              Default: <span className="text-slate-400">{wingetUninstallSwitchesDefault}</span>
                            </p>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 italic">
                          These switches are passed to <span className="font-mono text-slate-400">Start-ADTProcess</span> inside <span className="font-mono text-slate-400">Invoke-AppDeployToolkit.ps1</span>.
                        </p>

                        {/* ARP-driven uninstall toggle (EXE-only). Defaults ON —
                            uses each app's registered QuietUninstallString rather
                            than re-running setup.exe (which almost never uninstalls
                            cleanly for apps like Firefox, Chrome, Notepad++). */}
                        <UninstallStrategyFields
                          useArp={wingetUseArpUninstall}
                          onUseArpChange={setWingetUseArpUninstall}
                          extraArgs={wingetArpExtraArgs}
                          onExtraArgsChange={setWingetArpExtraArgs}
                          customCommand={wingetCustomUninstallCommand}
                          onCustomCommandChange={setWingetCustomUninstallCommand}
                        />
                      </div>
                    </div>
                  )}

                  {/* Documented install switches that stop an EXE setting up
                      its own updater. Adding one turns the override on, since
                      only overridden switches are sent to /continue. */}
                  {wingetIsExe && preparedPackageId && (
                    <UpdateControlsPanel
                      apiBase={API}
                      packageId={preparedPackageId}
                      authHeaders={getAuthHeaders}
                      mode="exe"
                      disabled={preparingPackage || packaging}
                      installSwitches={wingetInstallSwitches}
                      onAddSwitch={(sw) => {
                        setWingetSwitchOverride(true);
                        setWingetInstallSwitches((cur) => `${(cur || "").trim()} ${sw}`.trim());
                      }}
                    />
                  )}

                  {/* Advanced Customization (Expert Mode) — always available */}
                  <div className="border border-slate-700 rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850 cursor-pointer hover:from-slate-750 hover:to-slate-800 transition-colors"
                      onClick={() => setExpertCustomMode(!expertCustomMode)}
                      data-testid="expert-custom-mode-toggle"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${expertCustomMode ? 'bg-orange-900/50' : 'bg-slate-700'}`}>
                          <FileCode className={`h-4 w-4 ${expertCustomMode ? 'text-orange-400' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-slate-200">Advanced Customization (Expert Mode)</h4>
                          <p className="text-xs text-slate-400">
                            Upload extra files & set registry entries — injected at PSADT v4 phase markers
                            {(expertFiles.length + expertRegistry.length) > 0 && (
                              <span className="ml-2 text-orange-300">
                                · {expertFiles.length} file(s), {expertRegistry.length} reg entry(ies)
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 hidden sm:inline">Expert Mode</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={expertCustomMode}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            expertCustomMode ? 'bg-orange-600' : 'bg-slate-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpertCustomMode(!expertCustomMode);
                          }}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              expertCustomMode ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    {expertCustomMode && (
                      <div className="p-3 border-t border-slate-700 bg-slate-900/60">
                        <ExpertModeCustomizations
                          customFiles={expertFiles}
                          setCustomFiles={setExpertFiles}
                          registryEntries={expertRegistry}
                          setRegistryEntries={setExpertRegistry}
                          apiBase={API}
                          getAuthHeaders={getAuthHeaders}
                          disabled={preparingPackage || packaging}
                        />
                      </div>
                    )}
                  </div>

                  {/* MSI Properties — only rendered for MSI installers */}
                  {downloadedMsiProps && (
                  <>
                  {/* Auto-update controls the MSI exposes (properties,
                      registry values, updater features and services). */}
                  {preparedPackageId && (
                    <UpdateControlsPanel
                      apiBase={API}
                      packageId={preparedPackageId}
                      authHeaders={getAuthHeaders}
                      mode="msi"
                      disabled={preparingPackage || packaging}
                      propertyOverrides={msiPropsEnabled}
                      onApplyProperty={(name, value) => {
                        // An empty override set means "use the defaults", so
                        // seed them first or ALLUSERS etc. would be dropped.
                        setMsiPropsEnabled((p) => ({
                          ...(Object.keys(p).length > 0 ? p : DEFAULT_MSI_PROPERTIES),
                          [name]: value,
                        }));
                        toast.success(`${name}=${value} added to the MSI properties`);
                      }}
                      useMst={useMst}
                      onEnableMst={() => {
                        setUseMst(true);
                        toast.info("MST mode turned on. Apply the suggestion again to queue it.");
                      }}
                      onQueueTableEdit={(table, edit) => {
                        if (!mstOverridesRef.current) return false;
                        mstOverridesRef.current.queueEdit(table, edit);
                        return true;
                      }}
                    />
                  )}

                  {/* Phase 1 — MSI Transform (MST) toggle */}
                  <div className={`border rounded-lg overflow-hidden ${useMst ? "border-cyan-500/60 bg-cyan-500/5" : "border-slate-700"}`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-800/50 transition-colors"
                      onClick={() => setUseMst(!useMst)}
                      data-testid="mst-mode-toggle-package-modal"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${useMst ? "bg-cyan-500/20" : "bg-slate-700"}`}>
                          <FileText className={`h-4 w-4 ${useMst ? "text-cyan-400" : "text-slate-400"}`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-slate-200 flex items-center gap-2">
                            Bake as MSI Transform (MST)
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/40">
                              Phase 1
                            </span>
                          </h4>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {useMst
                              ? "Property overrides will be baked into alice.mst — vendor MSI stays byte-perfect, PSADT uses -Transforms."
                              : "Off — properties passed via -AdditionalArgumentList on the command line (legacy behaviour)."}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={useMst}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          useMst ? "bg-cyan-600" : "bg-slate-600"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUseMst(!useMst);
                        }}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            useMst ? "translate-x-5" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                    {useMst && (
                      <div className="px-3 pb-3 pt-1 text-[11px] text-cyan-200/80 border-t border-cyan-500/20">
                        <p className="italic mb-2">
                          Immutable properties (ProductCode, UpgradeCode, PackageCode) are automatically excluded. On any MST
                          generation error, packaging falls back to CLI mode so builds never fail hard. Validate on your
                          Windows test devices before rolling to production — first-generation MSTs may need iteration.
                        </p>
                        {/* Phase 3 — Advanced Overrides for Registry / Feature /
                            FeatureComponents. Only rendered when we already
                            have a preparedPackageId (base MSI on the server). */}
                        {preparedPackageId && (
                          <div className="mt-3 pt-3 border-t border-cyan-500/10">
                            <h5 className="text-xs font-medium text-cyan-100 mb-2">
                              Advanced table overrides
                            </h5>
                            <MstAdvancedOverrides
                              ref={mstOverridesRef}
                              apiBase={API}
                              packageId={preparedPackageId}
                              authHeaders={getAuthHeaders}
                              disabled={preparingPackage || packaging}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border border-slate-700 rounded-lg overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850 cursor-pointer hover:from-slate-750 hover:to-slate-800 transition-colors"
                      onClick={() => setMsiExpertMode(!msiExpertMode)}
                      data-testid="msi-expert-mode-toggle-dashboard"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${msiExpertMode ? 'bg-orange-900/50' : 'bg-slate-700'}`}>
                          <Settings className={`h-4 w-4 ${msiExpertMode ? 'text-orange-400' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm text-slate-200">MSI Properties</h4>
                          <p className="text-xs text-slate-400">
                            {downloadedMsiProps?.stats?.total || 0} total
                            {downloadedMsiProps?.stats?.public > 0 && <> • <span className="text-green-400 font-medium">{downloadedMsiProps.stats.public} public</span></>}
                            {downloadedMsiProps?.stats?.secure_custom > 0 && <> • <span className="text-blue-400 font-medium">{downloadedMsiProps.stats.secure_custom} app config</span></>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 hidden sm:inline">Expert Mode</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={msiExpertMode}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            msiExpertMode ? 'bg-orange-600' : 'bg-slate-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMsiExpertMode(!msiExpertMode);
                          }}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              msiExpertMode ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    
                    {/* Expert Mode Content - Collapsible */}
                    {msiExpertMode && (
                      <div className="p-3 border-t border-slate-700 space-y-3 bg-slate-900">
                        <p className="text-xs text-slate-400">
                          Only <strong className="text-slate-200">PUBLIC properties</strong> (ALL UPPERCASE) can be overridden via command line.
                        </p>
                        
                        {/* Property Stats */}
                        {downloadedMsiProps?.stats && (
                          <div className="flex gap-4 text-xs text-slate-400">
                            <span>Total: <strong className="text-slate-200">{downloadedMsiProps.stats.total}</strong></span>
                            <span>Public: <strong className="text-green-400">{downloadedMsiProps.stats.public}</strong></span>
                            <span>Private: <strong className="text-orange-400">{downloadedMsiProps.stats.private}</strong></span>
                          </div>
                        )}
                  
                  {/* SecureCustomProperties - Application-specific configuration */}
                  {downloadedMsiProps?.secure_custom_properties && downloadedMsiProps.secure_custom_properties.length > 0 && (
                    <div className="border border-blue-700 rounded-lg overflow-hidden">
                      <div className="bg-blue-950/50 px-3 py-1.5 border-b border-blue-700">
                        <span className="text-xs font-semibold text-blue-300">
                          Application Configuration (SecureCustomProperties)
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 px-3 py-1.5 bg-blue-950/30 border-b border-blue-800">
                        These properties are defined by the application for custom configuration (e.g., server URLs, license keys).
                      </p>
                      <div className="p-3 space-y-3 bg-slate-900/50">
                        {downloadedMsiProps.secure_custom_properties.map((prop, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-medium text-slate-200">{prop.name}</span>
                                <Badge variant="outline" className="text-[10px] py-0 border-blue-500 text-blue-400">custom</Badge>
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {prop.description || "Application-specific configuration"}
                              </p>
                              {prop.value && (
                                <p className="text-xs text-slate-500 font-mono">Default: {prop.value}</p>
                              )}
                            </div>
                            <div className="w-[200px]">
                              <Input
                                value={msiPropsEnabled[prop.name] ?? prop.value ?? ""}
                                onChange={(e) => setMsiPropsEnabled(p => ({...p, [prop.name]: e.target.value}))}
                                placeholder={prop.value || "Enter value..."}
                                className="h-8 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Public Properties Table (Editable) */}
                  {downloadedMsiProps?.public_properties && downloadedMsiProps.public_properties.length > 0 ? (
                    <div className="border border-green-800 rounded-lg overflow-hidden">
                      <div className="bg-green-950/50 px-3 py-1.5 border-b border-green-800">
                        <span className="text-xs font-semibold text-green-400">PUBLIC Properties (Can Override)</span>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-800 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold text-slate-200 border-b border-slate-700">Property</th>
                              <th className="text-left py-2 px-3 font-semibold text-slate-200 border-b border-slate-700">Value</th>
                              <th className="text-left py-2 px-3 font-semibold text-slate-200 border-b border-slate-700 w-[100px]">Override</th>
                            </tr>
                          </thead>
                          <tbody className="bg-slate-900/50">
                            {downloadedMsiProps.public_properties.map((prop, idx) => (
                              <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                                <td className="py-2 px-3 font-mono text-slate-200">
                                  {prop.name}
                                  {prop.recommended && (
                                    <Badge variant="outline" className="ml-2 text-[10px] py-0 border-green-400/40 text-green-400">recommended</Badge>
                                  )}
                                  {prop.category === 'product' && (
                                    <Badge variant="outline" className="ml-2 text-[10px] py-0 border-blue-500 text-blue-400">product</Badge>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {msiPropsEnabled[prop.name] !== undefined ? (
                                    <Input
                                      value={msiPropsEnabled[prop.name] || ''}
                                      onChange={(e) => setMsiPropsEnabled(p => ({...p, [prop.name]: e.target.value}))}
                                      className="h-7 text-xs font-mono bg-slate-800 border-slate-600 text-slate-200"
                                      disabled={!prop.editable}
                                    />
                                  ) : (
                                    <span className="font-mono text-slate-400 truncate block max-w-[200px]" title={prop.value}>
                                      {prop.value}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {prop.editable ? (
                                    <Checkbox
                                      checked={msiPropsEnabled[prop.name] !== undefined}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setMsiPropsEnabled(p => ({...p, [prop.name]: prop.value}));
                                        } else {
                                          setMsiPropsEnabled(p => {
                                            const newProps = {...p};
                                            delete newProps[prop.name];
                                            return newProps;
                                          });
                                        }
                                      }}
                                    />
                                  ) : (
                                    <span className="text-slate-500 text-[10px]">read-only</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : downloadedMsiProps && (!downloadedMsiProps.public_properties || downloadedMsiProps.public_properties.length === 0) && (
                    <div className="border border-yellow-700 rounded-lg p-3 bg-yellow-950/30">
                      <p className="text-xs text-yellow-400 font-medium flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Could not extract MSI properties
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        The MSI property table couldn't be read. This may be because msitools is not installed on the server. 
                        Use the Quick Add buttons below to manually configure common deployment properties.
                      </p>
                    </div>
                  )}
                  
                  {/* Private Properties Table (Read-Only) - Collapsed by Default */}
                  {downloadedMsiProps?.all_properties && downloadedMsiProps.all_properties.filter(p => !p.is_public).length > 0 && (
                    <details className="border border-orange-800 rounded-lg overflow-hidden">
                      <summary className="bg-orange-950/50 px-3 py-1.5 border-b border-orange-800 cursor-pointer hover:bg-orange-950/70">
                        <span className="text-xs font-semibold text-orange-400">
                          Private Properties ({downloadedMsiProps.all_properties.filter(p => !p.is_public).length}) - Read Only
                        </span>
                      </summary>
                      <div className="max-h-[150px] overflow-y-auto bg-slate-900/50">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-800 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold text-slate-200 border-b border-slate-700">Property</th>
                              <th className="text-left py-2 px-3 font-semibold text-slate-200 border-b border-slate-700">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {downloadedMsiProps.all_properties.filter(p => !p.is_public).map((prop, idx) => (
                              <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-800/50 opacity-70">
                                <td className="py-2 px-3 font-mono text-slate-400">{prop.name}</td>
                                <td className="py-2 px-3">
                                  <span className="font-mono text-slate-500 truncate block max-w-[300px]" title={prop.value}>
                                    {prop.value}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                      </div>
                    )}
                  </div>
                  
                  <MsiWarningsNotice warnings={msiWarnings} />
                  <MsiSuggestedNotice suggested={msiSuggested} />

                  {/* Quick Add Common Deployment Properties */}
                  <div className="p-3 border border-slate-700 rounded-lg bg-slate-800/50">
                    <p className="text-xs font-medium mb-2 text-slate-200">Quick Add Common Properties:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { name: 'ALLUSERS', value: '1', desc: 'All Users' },
                        { name: 'REBOOT', value: 'ReallySuppress', desc: 'No Reboot' },
                        { name: 'ARPNOMODIFY', value: '1', desc: 'Hide Modify' },
                        { name: 'ARPNOREMOVE', value: '1', desc: 'Hide Remove' },
                        { name: 'ARPNOREPAIR', value: '1', desc: 'Hide Repair' },
                      ].map(prop => (
                        <Button
                          key={prop.name}
                          variant={msiPropsEnabled[prop.name] !== undefined ? "default" : "outline"}
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => {
                            if (msiPropsEnabled[prop.name] !== undefined) {
                              setMsiPropsEnabled(p => {
                                const newProps = {...p};
                                delete newProps[prop.name];
                                return newProps;
                              });
                            } else {
                              setMsiPropsEnabled(p => ({...p, [prop.name]: prop.value}));
                            }
                          }}
                        >
                          {msiPropsEnabled[prop.name] !== undefined ? <Check className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                          {prop.desc}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Show override summary */}
                  {Object.keys(msiPropsEnabled).length > 0 && (
                    <div className="p-2 bg-orange-950/30 border border-orange-800 rounded text-xs">
                      <span className="font-medium text-orange-400">
                        {Object.keys(msiPropsEnabled).length} properties will be overridden:
                      </span>
                      <span className="ml-2 font-mono text-orange-300">
                        {Object.entries(msiPropsEnabled).map(([k,v]) => `${k}=${v}`).join(' ')}
                      </span>
                    </div>
                  )}
                  </>
                  )}
                  {/* MSI Shortcuts (Phase 1 — Option E).
                      Lists every row in the MSI's Shortcut table; admin
                      ticks which ones to strip on install. Selection
                      travels with the /continue payload and the PSADT
                      generator emits Remove-ADTFile blocks. Pane only
                      renders if the MSI declares at least one shortcut. */}
                  {downloadedMsiShortcuts && downloadedMsiShortcuts.length > 0 && (
                    <div
                      className="mt-4 border border-pink-700/40 rounded-lg bg-pink-950/20"
                      data-testid="msi-shortcuts-pane"
                    >
                      <button
                        type="button"
                        onClick={() => setMsiShortcutsExpanded(!msiShortcutsExpanded)}
                        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-pink-900/20 rounded-t-lg"
                        data-testid="msi-shortcuts-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-pink-200">
                            🪦 MSI Shortcuts
                          </span>
                          <span className="text-xs text-pink-300/80">
                            {downloadedMsiShortcuts.length} declared
                            {" · "}
                            {downloadedMsiShortcuts.filter((s) => s.is_desktop).length} on desktop
                            {" · "}
                            <span className="text-pink-300 font-medium">
                              {Object.values(shortcutRemovals).filter(Boolean).length} ticked for removal
                            </span>
                          </span>
                        </div>
                        <span className="text-xs text-pink-300/60">
                          {msiShortcutsExpanded ? "▾" : "▸"}
                        </span>
                      </button>
                      {msiShortcutsExpanded && (
                        <div className="px-3 pb-3 pt-1">
                          <p className="text-xs text-pink-200/70 mb-2">
                            Tick any shortcuts you'd like removed after install. We emit{" "}
                            <code className="bg-pink-900/40 px-1 rounded">Remove-ADTFile</code>{" "}
                            at the exact on-disk location reported by the MSI — Desktop entries
                            only touch Desktop, Start Menu entries hit their declared sub-folder
                            (and tidy up empty parent folders). Selections are inherited
                            automatically by auto-update versions.
                          </p>
                          <div className="flex items-center justify-end gap-2 mb-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const sel = { ...shortcutRemovals };
                                for (const s of downloadedMsiShortcuts) {
                                  if (s.is_desktop) sel[s.shortcut] = true;
                                }
                                setShortcutRemovals(sel);
                              }}
                              data-testid="msi-shortcuts-tick-desktop-btn"
                              className="text-xs"
                            >
                              Tick all desktop
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShortcutRemovals({})}
                              data-testid="msi-shortcuts-clear-btn"
                              className="text-xs"
                            >
                              Clear
                            </Button>
                          </div>
                          <div className="border border-pink-800/40 rounded divide-y divide-pink-900/30 max-h-[260px] overflow-y-auto bg-slate-900/40">
                            {downloadedMsiShortcuts.map((sc) => {
                              const id = sc.shortcut || sc.name;
                              const checked = !!shortcutRemovals[id];
                              return (
                                <label
                                  key={id}
                                  className="flex items-start gap-2 p-2 hover:bg-pink-900/10 cursor-pointer"
                                  data-testid={`msi-shortcut-row-${id}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 accent-pink-600"
                                    checked={checked}
                                    onChange={(e) =>
                                      setShortcutRemovals({
                                        ...shortcutRemovals,
                                        [id]: e.target.checked,
                                      })
                                    }
                                    data-testid={`msi-shortcut-checkbox-${id}`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-medium text-slate-200 truncate">
                                        {sc.name || sc.shortcut || "(unnamed)"}
                                      </span>
                                      {sc.is_desktop && (
                                        <span
                                          className="text-[10px] px-1.5 py-0.5 rounded bg-pink-700/40 text-pink-100 border border-pink-700/60"
                                          data-testid={`msi-shortcut-desktop-badge-${id}`}
                                        >
                                          DESKTOP
                                        </span>
                                      )}
                                      {sc.is_advertised && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200">
                                          ADVERTISED
                                        </span>
                                      )}
                                      {sc.feature && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-300">
                                          Feature: {sc.feature}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] text-slate-400 mt-0.5 space-y-0.5">
                                      {sc.target && (
                                        <div className="truncate">
                                          <span className="font-medium">Target:</span> {sc.target}
                                        </div>
                                      )}
                                      {sc.target_directory_id && (
                                        <div className="truncate">
                                          <span className="font-medium">Dir:</span>{" "}
                                          {sc.directory_chain && sc.directory_chain.length > 0
                                            ? sc.directory_chain.join(" ← ")
                                            : sc.target_directory_id}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPackageModal(false);
                setShowMsiConfig(false);
                setDownloadedMsiProps(null);
            setDownloadedMsiShortcuts([]);
            setShortcutRemovals({});
                setPreparedPackageId(null);
                setExpertFiles([]);
                setExpertRegistry([]);
                setExpertCustomMode(false);
                setWingetSwitchOverride(false);
                setWingetInstallSwitches("");
                setWingetUninstallSwitches("");
                setWingetInstallSwitchesDefault("");
                setWingetUninstallSwitchesDefault("");
                setWingetIsExe(false);
                setWingetIsMsix(false);
                setWingetInstallerType("");
                setWingetInstallerFilename("");
                setWingetMsixPackageFamilyName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePackage}
              disabled={packaging || preparingPackage}
              className="bg-[#002FA7] hover:bg-[#001d6c] text-white"
              data-testid="create-package-btn"
            >
              {preparingPackage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Downloading Installer...
                </>
              ) : packaging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Package...
                </>
              ) : showMsiConfig ? (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Continue with Packaging
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download & Configure
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Modal */}
      <Dialog open={showProgressModal} onOpenChange={setShowProgressModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] tracking-tight">
              Packaging Progress
            </DialogTitle>
            <DialogDescription>
              {activePackage?.app_name} - {activePackage?.version}
            </DialogDescription>
          </DialogHeader>

          {activePackage && (
            <div className="space-y-6 py-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{activePackage.current_step}</span>
                  <span className="text-white/90">{activePackage.progress}%</span>
                </div>
                <Progress value={activePackage.progress} className="h-2" />
              </div>

              {/* Steps */}
              <div className="space-y-3">
                {["download", "extract", "create_psadt"].map((step) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center ${getStatusIconClass(step, activePackage.steps_completed, activePackage.current_step?.toLowerCase().includes(step) ? step : "")}`}>
                      {getStatusIcon(step, activePackage.steps_completed, activePackage.current_step?.toLowerCase().includes(step) ? step : "")}
                    </div>
                    <span className="text-sm capitalize">
                      {step === "create_psadt" ? "Create PSADT Package" : step.replace("_", " ")}
                    </span>
                  </div>
                ))}
              </div>

              {/* Status */}
              {activePackage.status === "completed" && (
                <div className="space-y-3">
                  <div className="bg-green-500/10 border border-green-400/40/30 rounded-lg p-4 flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-400" />
                    <div>
                      <p className="font-medium text-green-300">PSADT Package Ready</p>
                      <p className="text-sm text-green-400">Your package has been created. You can download it or upload to Intune from the Packages page.</p>
                    </div>
                  </div>
                  
                  {/* Direct Download Link (Workaround) */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="font-medium text-blue-300 mb-2 text-sm">📥 Direct Download Link:</p>
                    <a
                      href={`${API}/packages/${activePackage.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline break-all text-xs"
                    >
                      {`${API}/packages/${activePackage.id}/download`}
                    </a>
                    <p className="text-xs text-blue-400 mt-2">
                      💡 Tip: Click the link above to download in a new tab
                    </p>
                  </div>
                </div>
              )}

              {activePackage.status === "failed" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div>
                      <p className="font-medium text-red-300">Package Failed</p>
                      <p className="text-sm text-red-400">{activePackage.error_message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {activePackage?.status === "completed" && (
              <Button
                onClick={() => handleDownloadPackage(activePackage.id)}
                className="bg-[#002FA7] hover:bg-[#001d6c] text-white"
                data-testid="download-package-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Package
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowProgressModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => !open && resetUploadModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {uploadType === "select" ? "Custom Upload" : 
               uploadType === "completed" ? "Upload Completed Package" : 
               uploadType === "private-repo" ? "Package from Private Repository" :
               "Upload Installer Files"}
            </DialogTitle>
            <DialogDescription>
              {uploadType === "select" 
                ? "Choose how you want to add your application to ALICE."
                : uploadType === "completed"
                ? "Upload an already packaged application (.intunewin or PSADT .zip) for deployment."
                : uploadType === "private-repo"
                ? "Pick an app from your Azure Blob Private Repository. ALICE will download the installer and run the standard analyser — no upload from your machine."
                : "Upload installer files to package with PSADT for Intune deployment."}
            </DialogDescription>
          </DialogHeader>

          {uploadType === "select" ? (
            // Upload Type Selection
            <div className={`grid ${privateRepoSettingsEnabled || (privateRepoCatalog && privateRepoCatalog.length > 0) ? "grid-cols-3" : "grid-cols-2"} gap-4 py-4`}>
              <Card 
                className="cursor-pointer hover:border-cyan-500 transition-colors"
                onClick={() => setUploadType("completed")}
                data-testid="upload-completed-package-option"
              >
                <CardContent className="p-6 text-center">
                  <Package className="h-12 w-12 mx-auto mb-4 text-cyan-500" />
                  <h3 className="font-semibold mb-2">Upload Completed Package</h3>
                  <p className="text-sm text-white/90">
                    Upload an already packaged app (.intunewin or PSADT .zip) ready for deployment
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1 justify-center">
                    <Badge variant="outline">.intunewin</Badge>
                    <Badge variant="outline">.zip</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-orange-500 transition-colors"
                onClick={() => setUploadType("installer")}
                data-testid="upload-installer-option"
              >
                <CardContent className="p-6 text-center">
                  <FileCode className="h-12 w-12 mx-auto mb-4 text-orange-500" />
                  <h3 className="font-semibold mb-2">Upload Installer Files</h3>
                  <p className="text-sm text-white/90">
                    Upload MSI/EXE or a folder with multiple files to package with PSADT
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1 justify-center">
                    <Badge variant="outline">.msi</Badge>
                    <Badge variant="outline">.exe</Badge>
                    <Badge variant="outline">.zip (folder)</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Private Repository picker — only shown when the tenant
                  has actually configured a Private Repo (settings enabled
                  OR catalog already has rows). Saves visual clutter for
                  customers who don't use the feature. */}
              {(privateRepoSettingsEnabled || (privateRepoCatalog && privateRepoCatalog.length > 0)) && (
                <Card
                  className="cursor-pointer hover:border-emerald-500 transition-colors"
                  onClick={() => setUploadType("private-repo")}
                  data-testid="upload-private-repo-option"
                >
                  <CardContent className="p-6 text-center">
                    <HardDrive className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <h3 className="font-semibold mb-2">Private Repository</h3>
                    <p className="text-sm text-white/90">
                      Package an app you&apos;ve already uploaded to your Azure Blob Private Repository
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1 justify-center">
                      {privateRepoCatalog === null || privateRepoCatalogLoading ? (
                        <Badge variant="outline" className="text-xs">Loading…</Badge>
                      ) : privateRepoCatalog.length === 0 ? (
                        <Badge variant="outline" className="text-xs">No apps detected</Badge>
                      ) : (
                        <>
                          <Badge variant="outline">{privateRepoCatalog.length} app{privateRepoCatalog.length === 1 ? "" : "s"}</Badge>
                          <Badge variant="outline">
                            {privateRepoCatalog.reduce((acc, f) => acc + (f.version_count || 0), 0)} versions
                          </Badge>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : uploadType === "private-repo" ? (
            // ---- Private Repository family/version picker ----
            <div className="space-y-4 py-2" data-testid="private-repo-picker">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    type="text"
                    placeholder="Filter apps…"
                    value={privateRepoSearch}
                    onChange={(e) => setPrivateRepoSearch(e.target.value)}
                    className="bg-white/5 border-white/10"
                    data-testid="private-repo-picker-search"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={rescanPrivateRepoFromModal}
                  disabled={privateRepoRescanning || privateRepoCatalogLoading}
                  data-testid="private-repo-picker-rescan"
                  className="border-emerald-500/50"
                >
                  {privateRepoRescanning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <HardDrive className="h-4 w-4 mr-2" />
                  )}
                  Rescan
                </Button>
              </div>

              {privateRepoCatalogLoading && privateRepoCatalog === null ? (
                <div className="flex items-center justify-center py-12 text-white/70">
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Loading catalog…
                </div>
              ) : privateRepoCatalogError ? (
                <Alert className="border-red-500/40 bg-red-500/10">
                  <AlertTitle>Couldn&apos;t load catalog</AlertTitle>
                  <AlertDescription className="text-sm">
                    {privateRepoCatalogError}
                  </AlertDescription>
                </Alert>
              ) : (privateRepoCatalog || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-emerald-500/40 p-8 text-center space-y-3" data-testid="private-repo-picker-empty">
                  <HardDrive className="h-10 w-10 mx-auto text-emerald-500/70" />
                  <p className="font-medium">No apps detected in your Private Repository</p>
                  <p className="text-xs text-white/70 max-w-md mx-auto">
                    Drop an installer into the dedicated container — folder layout{" "}
                    <code className="bg-white/10 px-1 rounded">{"{AppFamily}/{Version}/{Filename}"}</code>{" "}
                    — then click <strong>Rescan</strong>.
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => navigate("/settings")}
                    className="text-emerald-400"
                    data-testid="private-repo-picker-open-settings"
                  >
                    Open Settings → Storage
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    className="max-h-72 overflow-y-auto rounded-md border border-white/10 divide-y divide-white/10"
                    data-testid="private-repo-picker-list"
                  >
                    {(privateRepoCatalog || [])
                      .filter((f) =>
                        !privateRepoSearch ||
                        (f.family_name || "").toLowerCase().includes(privateRepoSearch.toLowerCase()),
                      )
                      .map((family) => {
                        const isSelected = privateRepoSelectedFamily === family.family_name;
                        return (
                          <button
                            type="button"
                            key={family.family_name}
                            onClick={() => {
                              setPrivateRepoSelectedFamily(family.family_name);
                              setPrivateRepoSelectedVersion(family.latest_version || "");
                            }}
                            className={`w-full text-left px-4 py-3 transition-colors flex items-center justify-between gap-3 ${
                              isSelected ? "bg-emerald-500/15 ring-1 ring-emerald-500" : "hover:bg-white/5"
                            }`}
                            data-testid={`private-repo-picker-family-${family.family_name}`}
                          >
                            <div className="min-w-0">
                              <div className="font-medium truncate">{family.family_name}</div>
                              <div className="text-xs text-white/60 truncate">
                                {family.version_count} version{family.version_count === 1 ? "" : "s"}
                                {family.versions && family.versions.length > 0
                                  ? " · " + family.versions.slice(0, 4).map((v) => `v${v.version}`).join(", ") +
                                    (family.versions.length > 4 ? "…" : "")
                                  : ""}
                              </div>
                            </div>
                            <Badge variant="outline" className={isSelected ? "border-emerald-500/60 text-emerald-300" : ""}>
                              Latest v{family.latest_version}
                            </Badge>
                          </button>
                        );
                      })}
                    {/* Empty filter result */}
                    {privateRepoSearch &&
                      (privateRepoCatalog || []).filter((f) =>
                        (f.family_name || "").toLowerCase().includes(privateRepoSearch.toLowerCase()),
                      ).length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-white/60">
                          No apps match &quot;{privateRepoSearch}&quot;.
                        </div>
                      )}
                  </div>

                  {privateRepoSelectedFamily && (() => {
                    const selectedRow = (privateRepoCatalog || []).find(
                      (f) => f.family_name === privateRepoSelectedFamily,
                    );
                    const versions = selectedRow?.versions || [];
                    return (
                      <div className="rounded-md bg-white/5 border border-white/10 p-4 space-y-2" data-testid="private-repo-picker-version">
                        <Label htmlFor="private-repo-version-select" className="text-sm">
                          Version to package
                        </Label>
                        <select
                          id="private-repo-version-select"
                          value={privateRepoSelectedVersion}
                          onChange={(e) => setPrivateRepoSelectedVersion(e.target.value)}
                          className="w-full bg-slate-900 border border-white/20 rounded-md px-3 py-2 text-sm"
                          data-testid="private-repo-picker-version-select"
                        >
                          {versions.map((v) => (
                            <option key={v.version} value={v.version}>
                              v{v.version}
                              {v.version === selectedRow.latest_version ? " (latest)" : ""}
                              {v.installer_filename ? ` · ${v.installer_filename}` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-white/60">
                          ALICE downloads this installer from your Azure Blob, runs the same analyser as a manual upload,
                          and opens the configuration screen — no file upload from your machine.
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="ghost" size="sm" onClick={() => setUploadType("select")} data-testid="private-repo-picker-back">
                  ← Back to options
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const selectedRow = (privateRepoCatalog || []).find(
                      (f) => f.family_name === privateRepoSelectedFamily,
                    );
                    if (!selectedRow) {
                      toast.error("Please select an app first.");
                      return;
                    }
                    triggerPrivateRepoPackage(privateRepoSelectedFamily, {
                      app_name: privateRepoSelectedFamily,
                      version: privateRepoSelectedVersion || selectedRow.latest_version,
                    });
                  }}
                  disabled={!privateRepoSelectedFamily}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="private-repo-picker-continue"
                >
                  Continue →
                </Button>
              </div>
            </div>
          ) : uploadType === "completed" && !completedPackageData ? (
            // Completed Package Upload
            <div className="space-y-4 py-4">
              <div className="border-2 border-dashed border-cyan-500/50 rounded-lg p-8 text-center">
                <Package className="h-12 w-12 mx-auto text-cyan-500 mb-4" />
                <p className="text-sm text-white/90 mb-4">
                  Upload your pre-packaged application file
                </p>
                <input
                  type="file"
                  accept=".intunewin,.zip"
                  onChange={handleCompletedPackageUpload}
                  className="hidden"
                  id="completed-package-upload"
                  disabled={uploadingFile}
                />
                <label htmlFor="completed-package-upload">
                  <Button variant="outline" asChild disabled={uploadingFile} className="border-cyan-500/50">
                    <span>
                      {uploadingFile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Select Package
                        </>
                      )}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-white/90 mt-4">
                  Supported formats: <strong>.intunewin</strong>, <strong>.zip</strong> (PSADT package)
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setUploadType("select")}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to options
              </Button>
            </div>
          ) : uploadType === "completed" && completedPackageData ? (
            // Completed Package Details
            <div className="space-y-4 py-4">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Package className="h-10 w-10 text-cyan-500" />
                  <div className="flex-1">
                    <p className="font-medium">{completedPackageData.filename}</p>
                    <p className="text-sm text-white/90">
                      {completedPackageData.file_size_mb} MB • {completedPackageData.package_type}
                    </p>
                  </div>
                  <Badge className="bg-cyan-500">Ready</Badge>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="completed-app-name">Application Name *</Label>
                    <Input 
                      id="completed-app-name"
                      value={uploadAppName}
                      onChange={(e) => setUploadAppName(e.target.value)}
                      placeholder="e.g., 7-Zip"
                    />
                  </div>
                  <div>
                    <Label htmlFor="completed-version">Version *</Label>
                    <Input 
                      id="completed-version"
                      value={uploadVersion}
                      onChange={(e) => setUploadVersion(e.target.value)}
                      placeholder="e.g., 24.09"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="completed-publisher">Publisher</Label>
                  <Input 
                    id="completed-publisher"
                    value={uploadPublisher}
                    onChange={(e) => setUploadPublisher(e.target.value)}
                    placeholder="e.g., Igor Pavlov"
                  />
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => { setCompletedPackageData(null); setUploadType("select"); }}>
                  Back
                </Button>
                <Button 
                  onClick={handleCreateCompletedPackage}
                  disabled={!uploadAppName || !uploadVersion || uploadingFile}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {uploadingFile ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Add to ALICE
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : uploadType === "installer" && !uploadedFileData && privateRepoMode ? (
            // Private Repository: file is being fetched from the customer's
            // blob and analysed server-side. No file picker is shown.
            <div className="space-y-4 py-6" data-testid="private-repo-fetching">
              <div className="border-2 border-dashed border-emerald-500/50 rounded-lg p-10 text-center">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
                  <div className="space-y-1">
                    <p className="font-medium text-emerald-200">
                      Fetching from Private Repository…
                    </p>
                    <p className="text-xs text-white/70 max-w-sm">
                      Downloading the latest installer for <strong>{uploadAppName || "this app"}</strong>{" "}
                      from your Azure Blob Private Repository and running MSI/EXE analysis.
                      You won't need to upload anything.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={resetUploadModal} data-testid="private-repo-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          ) : uploadType === "installer" && !uploadedFileData ? (
            // Installer File Upload Step
            <div className="space-y-4 py-4">
              <div className="border-2 border-dashed border-orange-500/50 rounded-lg p-8 text-center">
                <FileCode className="h-12 w-12 mx-auto text-orange-500 mb-4" />
                <p className="text-sm text-white/90 mb-4">
                  Upload a single installer or a ZIP containing multiple files
                </p>
                <input
                  type="file"
                  accept=".msi,.exe,.zip"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={uploadingFile}
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" asChild disabled={uploadingFile} className="border-orange-500/50">
                    <span>
                      {uploadingFile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Select File
                        </>
                      )}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-white/90 mt-4">
                  Supported: <strong>.msi</strong>, <strong>.exe</strong>, or <strong>.zip</strong> (folder with multiple files)
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setUploadType("select")}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to options
              </Button>
            </div>
          ) : uploadedFileData?.is_folder ? (
            // Folder/ZIP Upload - Select Main Installer
            <div className="space-y-4 py-4">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FolderOpen className="h-10 w-10 text-orange-500" />
                  <div className="flex-1">
                    <p className="font-medium">Folder Contents Extracted</p>
                    <p className="text-sm text-white/90">
                      {extractedFiles.length} files found
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-base">Select Main Installer *</Label>
                <p className="text-sm text-white/90">
                  Choose which file is the primary installer (e.g., setup.exe)
                </p>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {extractedFiles.map((file, idx) => {
                    const isInstaller = file.toLowerCase().endsWith('.exe') || file.toLowerCase().endsWith('.msi');
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                          selectedMainInstaller === file ? 'bg-orange-500/10 border-orange-500/50' : ''
                        }`}
                        onClick={() => isInstaller && setSelectedMainInstaller(file)}
                      >
                        <input
                          type="radio"
                          name="main-installer"
                          checked={selectedMainInstaller === file}
                          onChange={() => setSelectedMainInstaller(file)}
                          disabled={!isInstaller}
                          className="h-4 w-4"
                        />
                        <FileCode className={`h-4 w-4 ${isInstaller ? 'text-orange-500' : 'text-white/90'}`} />
                        <span className={`text-sm flex-1 ${!isInstaller && 'text-white/90'}`}>
                          {file}
                        </span>
                        {isInstaller && (
                          <Badge variant="outline" className="text-xs">
                            {file.toLowerCase().endsWith('.msi') ? 'MSI' : 'EXE'}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="folder-app-name">Application Name *</Label>
                  <Input
                    id="folder-app-name"
                    value={uploadAppName}
                    onChange={(e) => setUploadAppName(e.target.value)}
                    placeholder="e.g., My Application"
                  />
                </div>
                <div>
                  <Label htmlFor="folder-version">Version *</Label>
                  <Input
                    id="folder-version"
                    value={uploadVersion}
                    onChange={(e) => setUploadVersion(e.target.value)}
                    placeholder="e.g., 1.0.0"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => { setUploadedFileData(null); setExtractedFiles([]); }}>
                  Back
                </Button>
                <Button
                  onClick={handleFolderPackage}
                  disabled={!selectedMainInstaller || !uploadAppName || !uploadVersion || packaging}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {packaging ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Packaging...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Create Package
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : uploadedFileData?.is_exe ? (
            // EXE Configuration Step
            <div className="space-y-4 py-4">
              {/* File Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FileCode className="h-10 w-10 text-orange-500" />
                  <div className="flex-1">
                    <p className="font-medium">{uploadedFileData.filename}</p>
                    <p className="text-sm text-white/90">
                      {uploadedFileData.file_size_mb} MB • EXE Installer
                    </p>
                  </div>
                  <Badge variant="outline" className={`ml-auto ${privateRepoMode ? "border-emerald-500/60 text-emerald-300" : ""}`} data-testid={privateRepoMode ? "private-repo-source-badge-exe" : undefined}>
                    {privateRepoMode ? "Private Repo" : "Analyzed"}
                  </Badge>
                </div>
              </div>

              {/* Detection Result */}
              {uploadedFileData.exe_analysis && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-sm">Installer Detection</h4>
                    <Badge variant={uploadedFileData.exe_analysis.confidence >= 0.5 ? "default" : "secondary"}>
                      {Math.round(uploadedFileData.exe_analysis.confidence * 100)}% confidence
                    </Badge>
                  </div>
                  
                  {/* Detected Type */}
                  <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-200 border-blue-500/30">
                    <div className="flex items-center gap-2 text-sm text-blue-300 dark:text-blue-200">
                      <Info className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">Detected Type:</span>
                      <Badge variant="outline" className="capitalize text-blue-400 border-blue-400/40">
                        {uploadedFileData.exe_analysis.detected_type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {uploadedFileData.exe_analysis.evidence?.length > 0 && (
                      <p className="text-xs text-blue-400 mt-2">
                        Evidence: {uploadedFileData.exe_analysis.evidence[0]?.detail}
                      </p>
                    )}
                  </div>

                  {/* Warnings */}
                  {uploadedFileData.exe_analysis.ui_hints?.warnings?.length > 0 && (
                    <div className="space-y-2">
                      {uploadedFileData.exe_analysis.ui_hints.warnings.map((warning, idx) => (
                        <div key={idx} className={`rounded-lg p-3 border flex items-start gap-2 text-sm ${
                          warning.severity === 'error' ? 'bg-red-500/10 border-red-200 border-red-500/30 text-red-300 dark:text-red-200' :
                          warning.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200' :
                          'bg-[rgba(229,239,250,0.1)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] text-white'
                        }`}>
                          <AlertTriangle className={`h-4 w-4 flex-shrink-0 ${
                            warning.severity === 'error' ? 'text-red-500' :
                            warning.severity === 'warning' ? 'text-yellow-500' : 'text-white/80'
                          }`} />
                          <span>{warning.message}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Not Verified Banner */}
                  <div className="bg-orange-50 dark:bg-orange-950/30 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                    <div className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-orange-700 dark:text-orange-300">Not Verified</span>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          Suggested switches are based on static analysis. They have not been verified by execution.
                          Test in a lab environment before deploying to production.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Editable App Info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Application Details</h4>
                  {uploadedFileData.pe_version_info && (
                    <Badge variant="outline" className="text-xs text-green-400 border-green-400/40">
                      <Check className="h-3 w-3 mr-1" />
                      From PE metadata
                    </Badge>
                  )}
                </div>
                
                {/* Show raw PE metadata if available */}
                {uploadedFileData.pe_version_info && (
                  <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 border border-green-200 dark:border-green-800 text-xs">
                    <div className="grid grid-cols-2 gap-2 text-white/90">
                      {uploadedFileData.pe_version_info.product_name && (
                        <div><span className="font-medium">Product:</span> {uploadedFileData.pe_version_info.product_name}</div>
                      )}
                      {uploadedFileData.pe_version_info.product_version && (
                        <div><span className="font-medium">Version:</span> {uploadedFileData.pe_version_info.product_version}</div>
                      )}
                      {uploadedFileData.pe_version_info.company_name && (
                        <div><span className="font-medium">Company:</span> {uploadedFileData.pe_version_info.company_name}</div>
                      )}
                      {uploadedFileData.pe_version_info.file_description && (
                        <div><span className="font-medium">Description:</span> {uploadedFileData.pe_version_info.file_description}</div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="exe-app-name" className="text-xs">Application Name</Label>
                    <Input
                      id="exe-app-name"
                      value={uploadAppName}
                      onChange={(e) => setUploadAppName(e.target.value)}
                      placeholder="Enter application name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="exe-version" className="text-xs">Version</Label>
                    <Input
                      id="exe-version"
                      value={uploadVersion}
                      onChange={(e) => setUploadVersion(e.target.value)}
                      placeholder="1.0.0"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="exe-publisher" className="text-xs">Publisher</Label>
                    <Input
                      id="exe-publisher"
                      value={uploadPublisher}
                      onChange={(e) => setUploadPublisher(e.target.value)}
                      placeholder="Enter publisher name"
                    />
                  </div>
                </div>
              </div>

              {/* Silent Install Switches */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Terminal className="h-4 w-4" />
                    Silent Install Configuration
                  </h4>
                </div>

                {/* Candidate Selector */}
                {uploadedFileData.exe_analysis?.candidates?.length > 1 && (
                  <div className="space-y-2">
                    <Label className="text-xs">Select Switch Template</Label>
                    <Select
                      value={selectedCandidate?.id || ''}
                      onValueChange={(value) => {
                        const candidate = uploadedFileData.exe_analysis.candidates.find(c => c.id === value);
                        if (candidate) {
                          setSelectedCandidate(candidate);
                          setExeInstallSwitches(candidate.install?.switches || '');
                          setExeUninstallSwitches(candidate.uninstall?.switches || '');
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {uploadedFileData.exe_analysis.candidates.map((candidate) => (
                          <SelectItem key={candidate.id} value={candidate.id}>
                            <div className="flex items-center gap-2">
                              <span className="capitalize">{candidate.installer_type?.replace(/_/g, ' ')}</span>
                              <Badge variant="outline" className="text-[10px]">
                                {Math.round(candidate.confidence * 100)}%
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid gap-3">
                  <div>
                    <Label htmlFor="exe-install-switches" className="text-xs">Install Switches</Label>
                    <Input
                      id="exe-install-switches"
                      value={exeInstallSwitches}
                      onChange={(e) => setExeInstallSwitches(e.target.value)}
                      placeholder="/S /VERYSILENT"
                      className="font-mono text-sm"
                    />
                    {selectedCandidate?.install?.notes?.length > 0 && (
                      <p className="text-xs text-white/90 mt-1">
                        {selectedCandidate.install.notes[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="exe-uninstall-switches" className="text-xs">Uninstall Switches</Label>
                    <Input
                      id="exe-uninstall-switches"
                      value={exeUninstallSwitches}
                      onChange={(e) => setExeUninstallSwitches(e.target.value)}
                      placeholder="/S /uninstall"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>

                <UninstallStrategyFields
                  testIdPrefix="exe-"
                  useArp={exeUseArpUninstall}
                  onUseArpChange={setExeUseArpUninstall}
                  extraArgs={exeArpExtraArgs}
                  onExtraArgsChange={setExeArpExtraArgs}
                  customCommand={exeCustomUninstallCommand}
                  onCustomCommandChange={setExeCustomUninstallCommand}
                />

                {/* Install Context */}
                <div>
                  <Label className="text-xs mb-2 block">Install Context</Label>
                  <RadioGroup
                    value={exeInstallContext}
                    onValueChange={setExeInstallContext}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="system" id="context-system" />
                      <Label htmlFor="context-system" className="text-sm font-normal">System (All Users)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="user" id="context-user" />
                      <Label htmlFor="context-user" className="text-sm font-normal">User (Current User)</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Auto-Uninstall Previous Versions Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-uninstall-toggle" className="text-sm font-medium cursor-pointer">
                      Auto-uninstall previous versions
                    </Label>
                    <p className="text-xs text-white/90">
                      Automatically remove older versions before installing (recommended)
                    </p>
                  </div>
                  <Switch
                    id="auto-uninstall-toggle"
                    checked={exeAutoUninstallPrevious}
                    onCheckedChange={setExeAutoUninstallPrevious}
                  />
                </div>
              </div>

              {/* Detection Rule */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Detection Rule
                </h4>
                <p className="text-xs text-white/90">
                  How should Intune detect if this app is installed?
                </p>

                <RadioGroup
                  value={exeDetectionType}
                  onValueChange={setExeDetectionType}
                  className="space-y-2"
                >
                  <div className={`flex items-center space-x-2 p-2 rounded border hover:bg-muted/50 ${exeDetectionType === 'script' ? 'border-primary bg-primary/5' : ''}`}>
                    <RadioGroupItem value="script" id="detection-script" />
                    <Label htmlFor="detection-script" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Terminal className="h-4 w-4" />
                        <span className="font-medium text-sm">Custom script</span>
                        <Badge variant="secondary" className="text-[10px]">Recommended</Badge>
                      </div>
                      <p className="text-xs text-white/90 mt-1">PowerShell script checks registry for app installation (auto-generated)</p>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 rounded border hover:bg-muted/50">
                    <RadioGroupItem value="file" id="detection-file" />
                    <Label htmlFor="detection-file" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4" />
                        <span className="font-medium text-sm">File exists</span>
                      </div>
                      <p className="text-xs text-white/90 mt-1">Check if a specific file exists</p>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-2 rounded border hover:bg-muted/50">
                    <RadioGroupItem value="registry" id="detection-registry" />
                    <Label htmlFor="detection-registry" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span className="font-medium text-sm">Registry key</span>
                      </div>
                      <p className="text-xs text-white/90 mt-1">Check if a registry key/value exists</p>
                    </Label>
                  </div>
                </RadioGroup>

                {/* Detection Script Input */}
                {exeDetectionType === 'script' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="detection-script-input" className="text-xs">PowerShell Detection Script</Label>
                      <span className="text-[10px] text-white/90">Auto-generated based on app info</span>
                    </div>
                    <Textarea
                      id="detection-script-input"
                      value={exeDetectionScript}
                      onChange={(e) => setExeDetectionScript(e.target.value)}
                      placeholder={`# Return exit code 0 if app is installed\nif (Test-Path "C:\\Program Files\\AppName\\app.exe") {\n    exit 0\n} else {\n    exit 1\n}`}
                      className="font-mono text-xs min-h-[200px]"
                    />
                  </div>
                )}

                {/* Detection Path/Value Inputs */}
                {exeDetectionType === 'file' && (
                  <div>
                    <Label htmlFor="detection-path" className="text-xs">File Path</Label>
                    <Input
                      id="detection-path"
                      value={exeDetectionPath}
                      onChange={(e) => setExeDetectionPath(e.target.value)}
                      placeholder="C:\Program Files\AppName\app.exe"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-white/90 mt-1">
                      Use full path. Common locations: C:\Program Files\, C:\Program Files (x86)\
                    </p>
                  </div>
                )}

                {exeDetectionType === 'registry' && (
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="detection-registry-path" className="text-xs">Registry Key</Label>
                      <Input
                        id="detection-registry-path"
                        value={exeDetectionPath}
                        onChange={(e) => setExeDetectionPath(e.target.value)}
                        placeholder="HKLM\SOFTWARE\AppName"
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="detection-registry-value" className="text-xs">Value Name (optional)</Label>
                      <Input
                        id="detection-registry-value"
                        value={exeDetectionValue}
                        onChange={(e) => setExeDetectionValue(e.target.value)}
                        placeholder="Version"
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Expert Mode — Custom Files & Registry (EXE custom upload) */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850 cursor-pointer hover:from-slate-750 hover:to-slate-800 transition-colors"
                  onClick={() => setExpertCustomMode(!expertCustomMode)}
                  data-testid="custom-upload-expert-mode-toggle-exe"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${expertCustomMode ? 'bg-orange-900/50' : 'bg-slate-700'}`}>
                      <FileCode className={`h-4 w-4 ${expertCustomMode ? 'text-orange-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-slate-200">Advanced Customization (Expert Mode)</h4>
                      <p className="text-xs text-slate-400">
                        Upload extra files &amp; set registry entries — injected at PSADT v4 phase markers
                        {(expertFiles.length + expertRegistry.length) > 0 && (
                          <span className="ml-2 text-orange-300">
                            · {expertFiles.length} file(s), {expertRegistry.length} reg entry(ies)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 hidden sm:inline">Expert Mode</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={expertCustomMode}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        expertCustomMode ? 'bg-orange-600' : 'bg-slate-600'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpertCustomMode(!expertCustomMode);
                      }}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          expertCustomMode ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                {expertCustomMode && (
                  <div className="p-3 border-t border-slate-700 bg-slate-900/60">
                    <ExpertModeCustomizations
                      customFiles={expertFiles}
                      setCustomFiles={setExpertFiles}
                      registryEntries={expertRegistry}
                      setRegistryEntries={setExpertRegistry}
                      apiBase={API}
                      getAuthHeaders={getAuthHeaders}
                      disabled={packaging}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            // MSI Configuration Step
            <div className="space-y-4 py-4">
              {/* File Info with Icon */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {/* App Icon - fetched from homepage URL */}
                  {uploadedFileData.icon || uploadedFileData.icon_fallback ? (
                    <img 
                      src={uploadedFileData.icon || uploadedFileData.icon_fallback}
                      alt={`${uploadedFileData.product_info?.app_name} icon`}
                      className="w-12 h-12 rounded-lg object-contain bg-white p-1 border"
                      onError={(e) => {
                        // Try fallback icon
                        if (e.target.src === uploadedFileData.icon && uploadedFileData.icon_fallback) {
                          e.target.src = uploadedFileData.icon_fallback;
                        } else {
                          // Show package icon as final fallback
                          e.target.style.display = 'none';
                          e.target.nextSibling?.classList.remove('hidden');
                        }
                      }}
                    />
                  ) : null}
                  <Package className={`h-10 w-10 text-primary ${uploadedFileData.icon || uploadedFileData.icon_fallback ? 'hidden' : ''}`} />
                  <div className="flex-1">
                    <p className="font-medium">{uploadedFileData.product_info?.app_name || uploadedFileData.filename}</p>
                    <p className="text-sm text-white/90">
                      {uploadedFileData.file_size_mb} MB • MSI Installer
                    </p>
                    {uploadedFileData.product_info?.homepage && (
                      <a 
                        href={uploadedFileData.product_info.homepage} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                      >
                        <Globe className="h-3 w-3" />
                        {uploadedFileData.product_info.homepage}
                      </a>
                    )}
                  </div>
                  <Badge variant="outline" className={`ml-auto ${privateRepoMode ? "border-emerald-500/60 text-emerald-300" : ""}`} data-testid={privateRepoMode ? "private-repo-source-badge-msi" : undefined}>
                    {privateRepoMode ? "Private Repo" : "Uploaded"}
                  </Badge>
                </div>
              </div>

              {/* Editable App Info */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Application Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="upload-app-name" className="text-xs">Application Name</Label>
                    <Input
                      id="upload-app-name"
                      value={uploadAppName}
                      onChange={(e) => setUploadAppName(e.target.value)}
                      placeholder="Enter application name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="upload-version" className="text-xs">Version</Label>
                    <Input
                      id="upload-version"
                      value={uploadVersion}
                      onChange={(e) => setUploadVersion(e.target.value)}
                      placeholder="1.0.0"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="upload-publisher" className="text-xs">Publisher</Label>
                    <Input
                      id="upload-publisher"
                      value={uploadPublisher}
                      onChange={(e) => setUploadPublisher(e.target.value)}
                      placeholder="Enter publisher name"
                    />
                  </div>
                </div>
              </div>

              {/* MSI Properties */}
              {uploadedFileData.msi_properties && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">MSI Properties Configuration</h4>
                  <p className="text-xs text-white/90">
                    Only <strong>PUBLIC properties</strong> (ALL UPPERCASE) can be overridden via command line.
                  </p>
                  
                  {/* Property Stats */}
                  {uploadedFileData.msi_properties.stats && (
                    <div className="flex gap-4 text-xs text-white/90">
                      <span>Total: <strong>{uploadedFileData.msi_properties.stats.total}</strong></span>
                      <span>Public: <strong className="text-green-400">{uploadedFileData.msi_properties.stats.public}</strong></span>
                      <span>Private: <strong className="text-orange-600">{uploadedFileData.msi_properties.stats.private}</strong></span>
                    </div>
                  )}

                  {/* Public Properties Table */}
                  {uploadedFileData.msi_properties.public_properties?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-green-50 dark:bg-green-950/30 px-3 py-1.5 border-b">
                        <span className="text-xs font-semibold text-green-400">PUBLIC Properties (Can Override)</span>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold border-b">Property</th>
                              <th className="text-left py-2 px-3 font-semibold border-b">Value</th>
                              <th className="text-left py-2 px-3 font-semibold border-b w-[80px]">Override</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadedFileData.msi_properties.public_properties.map((prop, idx) => (
                              <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                                <td className="py-2 px-3 font-mono">
                                  {prop.name}
                                  {prop.recommended && (
                                    <Badge variant="outline" className="ml-2 text-[10px] py-0 border-green-400/40 text-green-400">recommended</Badge>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {uploadMsiProps[prop.name] !== undefined ? (
                                    <Input
                                      value={uploadMsiProps[prop.name] || ''}
                                      onChange={(e) => setUploadMsiProps(p => ({...p, [prop.name]: e.target.value}))}
                                      className="h-7 text-xs font-mono"
                                    />
                                  ) : (
                                    <span className="font-mono text-white/90 truncate block max-w-[180px]" title={prop.value}>
                                      {prop.value}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  {prop.editable && (
                                    <Checkbox
                                      checked={uploadMsiProps[prop.name] !== undefined}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setUploadMsiProps(p => ({...p, [prop.name]: prop.value}));
                                        } else {
                                          setUploadMsiProps(p => {
                                            const newProps = {...p};
                                            delete newProps[prop.name];
                                            return newProps;
                                          });
                                        }
                                      }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <MsiSuggestedNotice suggested={uploadMsiSuggested} />

                  {/* Quick Add Buttons */}
                  <div className="p-3 border rounded-lg bg-muted/30">
                    <p className="text-xs font-medium mb-2">Quick Add Common Properties:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { name: 'ALLUSERS', value: '1', desc: 'All Users' },
                        { name: 'REBOOT', value: 'ReallySuppress', desc: 'No Reboot' },
                        { name: 'ARPNOMODIFY', value: '1', desc: 'Hide Modify' },
                        { name: 'ARPNOREMOVE', value: '1', desc: 'Hide Remove' },
                      ].map(prop => (
                        <Button
                          key={prop.name}
                          variant={uploadMsiProps[prop.name] !== undefined ? "default" : "outline"}
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => {
                            if (uploadMsiProps[prop.name] !== undefined) {
                              setUploadMsiProps(p => {
                                const newProps = {...p};
                                delete newProps[prop.name];
                                return newProps;
                              });
                            } else {
                              setUploadMsiProps(p => ({...p, [prop.name]: prop.value}));
                            }
                          }}
                        >
                          {prop.desc}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Private Properties (Collapsed) */}
                  {uploadedFileData.msi_properties.all_properties?.filter(p => !p.is_public).length > 0 && (
                    <details className="border rounded-lg overflow-hidden">
                      <summary className="bg-orange-50 dark:bg-orange-950/30 px-3 py-1.5 cursor-pointer hover:bg-orange-100">
                        <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                          Private Properties ({uploadedFileData.msi_properties.all_properties.filter(p => !p.is_public).length}) - Read Only
                        </span>
                      </summary>
                      <div className="max-h-[120px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <tbody>
                            {uploadedFileData.msi_properties.all_properties.filter(p => !p.is_public).map((prop, idx) => (
                              <tr key={idx} className="border-b border-border/50 opacity-60">
                                <td className="py-1 px-3 font-mono text-white/90">{prop.name}</td>
                                <td className="py-1 px-3 font-mono text-white/90 truncate max-w-[200px]">{prop.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Expert Mode — Custom Files & Registry (MSI custom upload) */}
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-800 to-slate-850 cursor-pointer hover:from-slate-750 hover:to-slate-800 transition-colors"
                  onClick={() => setExpertCustomMode(!expertCustomMode)}
                  data-testid="custom-upload-expert-mode-toggle-msi"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${expertCustomMode ? 'bg-orange-900/50' : 'bg-slate-700'}`}>
                      <FileCode className={`h-4 w-4 ${expertCustomMode ? 'text-orange-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-slate-200">Advanced Customization (Expert Mode)</h4>
                      <p className="text-xs text-slate-400">
                        Upload extra files &amp; set registry entries — injected at PSADT v4 phase markers
                        {(expertFiles.length + expertRegistry.length) > 0 && (
                          <span className="ml-2 text-orange-300">
                            · {expertFiles.length} file(s), {expertRegistry.length} reg entry(ies)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 hidden sm:inline">Expert Mode</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={expertCustomMode}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        expertCustomMode ? 'bg-orange-600' : 'bg-slate-600'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpertCustomMode(!expertCustomMode);
                      }}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          expertCustomMode ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                {expertCustomMode && (
                  <div className="p-3 border-t border-slate-700 bg-slate-900/60">
                    <ExpertModeCustomizations
                      customFiles={expertFiles}
                      setCustomFiles={setExpertFiles}
                      registryEntries={expertRegistry}
                      setRegistryEntries={setExpertRegistry}
                      apiBase={API}
                      getAuthHeaders={getAuthHeaders}
                      disabled={packaging}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetUploadModal}>
              Cancel
            </Button>
            {uploadedFileData && (
              <Button 
                onClick={handleContinueUpload} 
                disabled={
                  packaging || 
                  !uploadAppName || 
                  !uploadVersion ||
                  (uploadedFileData.is_exe && !exeInstallSwitches) ||
                  (uploadedFileData.is_exe && exeDetectionType === 'file' && !exeDetectionPath) ||
                  (uploadedFileData.is_exe && exeDetectionType === 'registry' && !exeDetectionPath) ||
                  (uploadedFileData.is_exe && exeDetectionType === 'script' && !exeDetectionScript)
                }
              >
                {packaging ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Package...
                  </>
                ) : (
                  <>
                    <Package className="h-4 w-4 mr-2" />
                    Create PSADT Package
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
